import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core'
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular'
import {
  NgbModal,
  NgbModalRef,
  NgbOffcanvas,
  NgbOffcanvasRef,
  NgbTooltipModule
} from '@ng-bootstrap/ng-bootstrap'
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
  AbstractControl,
  ValidatorFn
} from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { CommonModule } from '@angular/common'
import { ActivatedRoute, RouterModule } from '@angular/router'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { PaginationComponent } from '../../template/pagination/pagination.component'
import {
  NgxDaterangepickerBootstrapComponent,
  NgxDaterangepickerBootstrapDirective
} from 'ngx-daterangepicker-bootstrap'
import { CalendarOptions, DatesSetArg } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import multiMonthPlugin from '@fullcalendar/multimonth'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction' // if needed
import rrulePlugin from '@fullcalendar/rrule'
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  take,
  takeUntil,
  tap
} from 'rxjs'
import {
  ClockInClockOut,
  ClockInClockOutResponse
} from '../../../../store/clockIn-clockOut/clockIn-clockOut.state'
import {
  selectClockinclockouts,
  selectLoading,
  selectTodaysClockinoutRecords
} from '../../../../store/clockIn-clockOut/clockIn-clockOut.reducer'
import { selectUser, selectUserPermissions } from '../../../../store/auth/auth.selectors'
import { AuthState, User } from '../../../../store/auth/auth.state'
import {
  clearAttendanceState,
  createEvent,
  createEventFailure,
  createEventSuccess,
  deleteEvent,
  deleteEventFailure,
  deleteEventSuccess,
  loadEmployeeAttendance
} from '../../../../store/clockIn-clockOut/clockIn-clockOut.actions'
import { loadEmployees, resetEmployeeState } from '../../../../store/user/user.actions'
import { Business } from '../../../../store/business/business.state'
import { selectAllBusinesses } from '../../../../store/business/business.selectors'
import { loadBusinesses } from '../../../../store/business/business.actions'
import { HasPermissionDirective } from '../../../../directives/has-permission.directive'
import { selectAllUsers } from '../../../../store/user/user.selectors'
import { NgSelectModule } from '@ng-select/ng-select'
import { AttendancePolicyService } from '../../../../services/attendance-policy.service'
import { EmployeeRequestService } from '../../../../services/employee-request.service'
import {
  createEmployeeRequest,
  createEmployeeRequestFailure,
  createEmployeeRequestsSuccess
} from '../../../../store/employee-request/employee-request.actions'
import { Actions, ofType } from '@ngrx/effects'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

import { LeavePolicyService } from '../../../../services/leave-policy.service'
import { loadCountries } from '../../../../store/country/country.actions'
import { selectAllCountries } from '../../../../store/country/country.selectors'
import { Country, Timezone } from '../../../../store/country/country.state'
import { eps } from '../../../../services/url.service'
import { LeaveType } from '../../../../store/leave-policy/leave-policy.state'
import { TrimInputDirective } from '../../../../directives/trim-input.directive'
import { SharedModule } from '../../../../shared/shared.module'
import { noLeadingTrailingSpaces } from '../../../../validators/no-leadingTraillingSpace.validators'
import { TIMEZONE_ALIASES } from './timezone-aliases'
import { DeleteComponent } from '../../template/delete/delete.component'

declare var bootstrap: any // Add this if you haven't already
declare var $: any // Declare jQuery

interface TimezoneOption {
  label: string
  zoneName: string
  countryId: number
  countryName: string
}

interface DateRowPayload {
  itemId: string
  date: string
  startTime: string
  endTime: string
  selectionType: 'FullDay' | 'HalfDay'
  halfDayOption: 'FirstHalf' | 'SecondHalf' | null | undefined
}

const dateRows: DateRowPayload[] = []

export enum WorkStatus {
  PRESENT = 'Present',
  LEAVE = 'Leave',
  WEEKLYOFF = 'Weekly Off',
  HOLIDAY = 'Holiday',
  // LateLogin = 'Late Login',
  // Uninformed = 'Uninformed',
  ABSENT = 'Absent',
  BREAK = 'Break',
  CLOCKIN = 'Clock In/Out',
  ABSENTFIRSTHALF = 'Absent First Half',
  ABSENTSECONDHALF = 'Absent Second Half',
  LEAVEFIRSTHALF = 'Leave First Half',
  LEAVESECONDHALF = 'Leave Second Half',
  COMPOFFEARNED = 'Comp Off',
  JOININGDATE = 'Date of Joining',
  ATTENDANCEREGULARIZEREQUEST = 'Regularization - Requested',
  ATTENDANCEREGULARIZEREQUESTCANCELLED = 'Regularization - Cancelled',
  ATTENDANCEREGULARIZEREQUESTREJECTED = 'Regularization - Rejected',
  REGULARIZEPRESENT = 'Present - Regularized',
  LEAVEREQUEST = 'Leave - Requested',
  // LeaveAccepted= 'Leave Accepted',
  LEAVECANCELLED = 'Leave - Cancelled',
  LEAVEREJECTED = 'Leave - Rejected',
  COMPOFFAPPLIED = 'Credit Comp Off - Requested',
  COMPOFFREJECTED = 'Comp Off - Rejected',
  COMPOFFCANCELLED = 'Comp Off - Cancelled',
  COMPOFFLEAVEAPPLIED = 'Comp Off Leave - Requested',
  COMPOFFLEAVECANCELLED = 'Comp Off Leave - Cancelled',
  COMPOFFLEAVEAPPROVE = 'Comp Off Leave',
  COMPOFFLEAVEREJECTED = 'Comp Off Leave - Rejected',
  LEAVEFIRSTHALFAPPLIED = 'Leave First Half - Requested',
  LEAVEFIRSTHALFREJECTED = 'Leave First Half - Rejected',
  LEAVEFIRSTHALFCANCELLED = 'Leave First Half - Cancelled',
  LEAVESECONDHALFAPPLIED = 'Leave Second Half - Requested',
  LEAVESECONDHALFREJECTED = 'Leave Second Half - Rejected',
  LEAVESECONDHALFCANCELLED = 'Leave Second Half - Cancelled',
  LEAVECANCELREQUESTED = 'Leave - Cancel Requested',
  LEAVEFIRSTHALFCANCELREQUESTED = 'Leave First Half - Cancel Requested',
  LEAVESECONDHALFCANCELREQUESTED = 'Leave Second Half - Cancel Requested',
  ABSENTHALF = 'Absent for Half Day',
  PRESENTHALF = 'Present for Half Day',
  REGULARIZEPRESENTHALF = 'Present - Regularized for Half Day',
  JOININGDATEEMPLOYEE = 'Joining Date - Employee',
  JOININGDATECONTRACTOR = 'Joining Date - Contractor',
  JOININGDATETRAINEE = 'Joining Date - Trainee',
  LATE = 'Late',
  EARLY = 'Early',
  ABSENTHALFDUETOLATE = 'Absent for Half Day - Late',
  LEAVECANCELAPPROVED = 'Leave - Cancel Approved',
  LEAVEFIRSTHALFCANCELAPPROVED = 'Leave First Half - Cancel Approved',
  LEAVESECONDHALFCANCELAPPROVED = 'Leave Second Half - Cancel Approved',
  REGULARIZEPRESENTFIRSTHALF = 'Present - Regularized for First Half Day',
  REGULARIZEPRESENTSECONDHALF = 'Present - Regularized for Second Half Day',
  ATTENDANCEREGULARIZEREQUESTFIRSTHALFCANCELLED = 'Regularization - First Half Cancelled',
  ATTENDANCEREGULARIZEREQUESTSECONDHALFCANCELLED = 'Regularization - Second Half Cancelled',
  ATTENDANCEREGULARIZEREQUESTFIRSTHALFREJECTED = 'Regularization - First Half Rejected',
  ATTENDANCEREGULARIZEREQUESTSECONDHALFREJECTED = 'Regularization - Second Half Rejected',
  ATTENDANCEREGULARIZEREQUESTFIRSTHALF = 'Regularization - First Half Requested',
  ATTENDANCEREGULARIZEREQUESTSECONDHALF = 'Regularization - Second Half Requested'
}

@Component({
  selector: 'app-calander',
  imports: [
    CommonModule,
    RouterModule,
    CapitalizePipe,
    // PaginationComponent,
    NgbTooltipModule,
    // NgxDaterangepickerBootstrapDirective,
    // NgxDaterangepickerBootstrapComponent,
    FormsModule,
    ReactiveFormsModule,
    FullCalendarModule,
    HasPermissionDirective,
    NgSelectModule,
    SharedModule
  ],
  templateUrl: './calander.component.html',
  styleUrl: './calander.component.scss'
})
export class CalanderComponent implements OnInit, OnDestroy, AfterViewInit {
  private store = inject(Store) // Inject store using `inject()`
  private attendancePolicyService = inject(AttendancePolicyService)
  private leavePolicyService = inject(LeavePolicyService)
  private validRangeSet = false
  private skipNextDatesSet = false
  private employeeRequestService = inject(EmployeeRequestService)
  private route = inject(ActivatedRoute)
  attendances$: Observable<ClockInClockOutResponse[]> | undefined
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  employees$: Observable<User[]> = this.store.select(selectAllUsers)
  user: AuthState['user'] | null = null
  selectedBusinessId = ''
  selectedEmployeeId = ''
  businessIdss!: string[]
  offcanvasTitle = ''
  accordionCount = 0
  attendancePolicies$: any = [] //Assigned Attendance Policies
  form!: FormGroup
  rangeForm!: FormGroup
  private deleteHandler: any
  employeeId = ''
  offcanvasRef!: NgbOffcanvasRef
  private destroyed$ = new Subject<void>()
  private employeeJoiningDate = '' // add at class level if needed
  timezoneOptions: TimezoneOption[] = []
  // selectedZoneName = ''
  selectedZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone // default
  eps = eps //Endponits
  compoffVisible = false // Add this as a class property
  assignedLeavePolicies!: any[]
  userPermissions: Record<string, string[]> = {}

  countries$: Observable<Country[]>
  // selectedCountryId = ''
  countries: Country[] = []
  timezones: Timezone[] = []
  selectedCountryId: number | null = null
  @ViewChild('addBusinessModals') addBusinessModals!: TemplateRef<any>
  private modalRef!: NgbModalRef
  isLoading$ = this.store.pipe(select(selectLoading))
  eventId = ''
  @ViewChild('fullcalendar') calendarComponent!: FullCalendarComponent
  maxDateTime = ''
  minDateTime = ''
  private loadAttendancesTimeout: any = null
  private visibleRangeStart: Date | null = null
  private visibleRangeEnd: Date | null = null
  selectedUserId = ''
  selectedDate = ''
  selectedEventLabel = 'Event'
  employeesCount = 0

  calendarOptions: CalendarOptions = {
    plugins: [
      dayGridPlugin,
      // multiMonthPlugin,
      rrulePlugin,
      timeGridPlugin,
      interactionPlugin // Optional (for drag & drop, click, etc.)
    ],
    initialView: 'dayGridMonth',
    // datesSet: arg => this.loadAttendances(arg.start, arg.end),
    // datesSet: arg => {
    //   // Extract the month and year from `arg.start` (which is usually end of previous month)
    //   const calendarMonth = arg.view.currentStart.getUTCMonth() // 1 = Feb if start is Jan 29
    //   const calendarYear = arg.view.currentStart.getUTCFullYear()
    //   // console.log('month', calendarMonth + 1)

    //   // Set first day of selected month
    //   const fromDate = new Date(Date.UTC(calendarYear, calendarMonth, 1))

    //   // Set last day of selected month
    //   const toDate = new Date(Date.UTC(calendarYear, calendarMonth + 1, 0, 23, 59, 59, 999))

    //   this.loadAttendances(fromDate, toDate)
    // },
    datesSet: arg => {
      if (this.skipNextDatesSet) {
        this.skipNextDatesSet = false
        return
      }
      // Cancel previous timeout if user clicked fast
      if (this.loadAttendancesTimeout) {
        clearTimeout(this.loadAttendancesTimeout)
      }

      const calendarApi = this.calendarComponent?.getApi?.()
      if (calendarApi) {
        calendarApi.removeAllEvents()
      }

      // Wait 300ms before calling API (adjust as needed)
      this.loadAttendancesTimeout = setTimeout(() => {
        const calendarMonth = arg.view.currentStart.getUTCMonth() // 0-based
        const calendarYear = arg.view.currentStart.getUTCFullYear()

        const fromDate = new Date(Date.UTC(calendarYear, calendarMonth, 1))
        const toDate = new Date(Date.UTC(calendarYear, calendarMonth + 1, 0, 23, 59, 59, 999))

        this.visibleRangeStart = fromDate
        this.visibleRangeEnd = toDate

        this.loadAttendances(fromDate, toDate)
      }, 300) // 300ms debounce
    },
    themeSystem: 'bootstrap5',
    height: '700px', // Set a specific height
    validRange: {
      start: this.employeeJoiningDate // this should be a 'YYYY-MM-DD' string
    },
    timeZone: this.selectedZoneName, // Set this dynamically
    // dayCellDidMount: this.dayCellDidMount.bind(this),
    // timeZone: 'local',
    // timeZone: 'Asia/Kolkata',
    fixedWeekCount: false, // This allows dynamic number of weeks (4–6 rows)
    // views: {
    //     multiMonthFourMonth: {
    //       type: 'multiMonth',
    //       duration: { months: 1 }
    //     }
    //   },
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      // right: 'dayGridMonth,timeGridWeek,timeGridDay,multiMonth,multiYear' // Include your new views here
      right: 'dayGridMonth' // Include your new views here
    },
    views: {
      // multiMonth: {
      //   type: 'multiMonth',
      //   duration: { months: 3 }, // Show 3 months at a time
      //   buttonText: 'Multi-Month' // Button text in header
      // },
      // multiYear: {
      //   type: 'multiMonth',
      //   duration: { months: 12 }, // Show 12 months (1 year) at a time
      //   buttonText: 'Multi-Year' // Button text in header
      // }
    },
    weekends: true,
    events: [],
    editable: false,
    droppable: false,
    // eventOrder: 'extendedProps.orderPriority,start,title', // order by priority, then start time
    eventOrder: (a: any, b: any) => {
      const priorityA = a.extendedProps?.orderPriority ?? 99
      const priorityB = b.extendedProps?.orderPriority ?? 99
      return priorityA - priorityB
    },
    eventDidMount: info => {
      const event = info.event
      const eventElement = info.el
      // ✅ ADD THESE 3 LINES AT THE TOP - applies to ALL events
      // eventElement.style.fontWeight = '600'
      // eventElement.style.fontSize = '12px'
      // eventElement.style.setProperty('font-weight', '600', 'important')
      if (event.title === 'Birthday Party') {
        // Apply styles directly if necessary
        eventElement.style.backgroundColor = '#ff6347' // Tomato color
        eventElement.style.color = 'white' // White text
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }
      if (
        event.title === WorkStatus.CLOCKIN ||
        event.title === 'ClockOut' ||
        event.title === 'Clock In/Out'
      ) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = 'rgb(253, 216, 53, 0.6)' // Tomato color
        eventElement.style.backgroundColor = '#FFBC80' // Apricot tone for neutral action
        eventElement.style.setProperty('color', 'black', 'important')

        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }
      if (
        event.title === WorkStatus.ABSENT ||
        event.title === WorkStatus.ABSENTFIRSTHALF ||
        event.title === WorkStatus.ABSENTSECONDHALF ||
        event.title === WorkStatus.ABSENTHALF
      ) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = 'rgb(243, 32, 19)' // Tomato color
        eventElement.style.backgroundColor = '#FF5252' // 	Red-Orange (warning tone)

        eventElement.style.color = 'white !important' // White text
        // eventElement.style.setProperty('color', 'white', 'important')
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }
      // if (
      //   event.title === WorkStatus.LEAVE ||
      //   event.title === WorkStatus.LEAVEFIRSTHALF ||
      //   event.title === WorkStatus.LEAVESECONDHALF
      // ) {
      //   // Apply styles directly if necessary
      //   // eventElement.style.backgroundColor = 'rgb(167 46 38)' // Tomato color
      //   eventElement.style.backgroundColor = '#FFAB40' // Tomato color
      //   eventElement.style.color = 'white !important' // White text
      //   eventElement.style.border = 'none' // No border
      //   eventElement.style.fontSize = '12px'
      // }
      if (
        event.title === WorkStatus.LEAVEREQUEST ||
        event.title === WorkStatus.LEAVEFIRSTHALFAPPLIED ||
        event.title === WorkStatus.LEAVESECONDHALFAPPLIED
      ) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = 'rgb(191 84 52)' // Tomato color
        eventElement.style.backgroundColor = '#FFD180' // Light Amber/Peach

        // eventElement.style.color = 'black !important' // White text
        eventElement.style.setProperty('color', 'black', 'important')
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }
      if (
        event.extendedProps['workStatus'] === WorkStatus.LEAVE ||
        event.extendedProps['workStatus'] === WorkStatus.LEAVEFIRSTHALF ||
        event.extendedProps['workStatus'] === WorkStatus.LEAVESECONDHALF
      ) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = 'rgb(191 84 52)' // Tomato color
        eventElement.style.backgroundColor = '#FFAB40' // Light Amber/Peach

        // eventElement.style.color = 'black !important' // White text
        eventElement.style.setProperty('color', 'black', 'important')
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }
      if (
        event.title === WorkStatus.LEAVECANCELLED ||
        event.title === WorkStatus.LEAVEREJECTED ||
        event.title === WorkStatus.LEAVEFIRSTHALFCANCELLED ||
        event.title === WorkStatus.LEAVEFIRSTHALFREJECTED ||
        event.title === WorkStatus.LEAVESECONDHALFCANCELLED ||
        event.title === WorkStatus.LEAVESECONDHALFREJECTED
      ) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = 'rgb(110 31 7)' // Tomato color
        eventElement.style.backgroundColor = '#FF8A65' // Very light warm tone
        // eventElement.style.color = 'black !important' // White text
        eventElement.style.setProperty('color', 'black', 'important')
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }

      if (event.title === WorkStatus.COMPOFFAPPLIED) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = '#90CAF9' // Tomato color
        eventElement.style.backgroundColor = '#FFCA28' // Sunflower Yellow
        // eventElement.style.color = 'black !important' // White text
        eventElement.style.setProperty('color', 'black', 'important')
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }

      if (event.title === WorkStatus.COMPOFFEARNED) {
        // Apply styles directly if necessary
        eventElement.style.backgroundColor = '#81D4FA' // Tomato color
        // eventElement.style.color = 'black !important' // White text
        eventElement.style.setProperty('color', 'black', 'important')
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }

      if (
        event.title === WorkStatus.COMPOFFCANCELLED ||
        event.title === WorkStatus.COMPOFFREJECTED ||
        event.title === WorkStatus.COMPOFFLEAVEREJECTED ||
        event.title === WorkStatus.COMPOFFLEAVECANCELLED
      ) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = '#EF9A9A' // Tomato color
        eventElement.style.backgroundColor = '#FF8A80' // Light Vanilla Tint
        // eventElement.style.color = 'black !important' // White text
        eventElement.style.setProperty('color', 'black', 'important')
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }
      if (event.title === WorkStatus.HOLIDAY) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = 'rgb(182, 54, 211)' // Tomato color
        eventElement.style.backgroundColor = '#B39DDB' // Pale Orange / Cream //light purple

        eventElement.style.setProperty('color', 'white', 'important')

        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }
      // Apply custom background color based on status
      if (
        event.title === WorkStatus.PRESENT ||
        event.title === 'Late Login' ||
        event.title === WorkStatus.PRESENTHALF
      ) {
        eventElement.style.backgroundColor = '#CCFF90' // Yellow
        // eventElement.style.color = 'black !important'
        eventElement.style.setProperty('color', 'black', 'important')

        eventElement.style.fontSize = '12px'
      }

      if (
        event.title === WorkStatus.REGULARIZEPRESENT ||
        event.title === WorkStatus.REGULARIZEPRESENTHALF ||
        event.title === WorkStatus.REGULARIZEPRESENTFIRSTHALF ||
        event.title === WorkStatus.REGULARIZEPRESENTSECONDHALF
      ) {
        eventElement.style.backgroundColor = '#AEE571' // Yellow
        // eventElement.style.backgroundColor = '#E65100' //Dark Orange
        // eventElement.style.color = 'black !important'
        eventElement.style.setProperty('color', 'black', 'important')

        eventElement.style.fontSize = '12px'
      }
      if (
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUEST ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALF ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALF
      ) {
        eventElement.style.backgroundColor = '#FFF59D' // Yellow
        // eventElement.style.color = 'black !important'
        eventElement.style.setProperty('color', 'black', 'important')

        eventElement.style.fontSize = '12px'
      }
      if (
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTCANCELLED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTREJECTED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALFCANCELLED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALFREJECTED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALFCANCELLED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALFREJECTED
      ) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = '#FFCDD2' // Tomato color
        eventElement.style.backgroundColor = '#FFCCBC' //Light Salmon
        // eventElement.style.color = 'white !important' // White text
        eventElement.style.setProperty('color', 'white', 'important')
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }

      if (
        event.title === WorkStatus.JOININGDATE ||
        event.title === WorkStatus.JOININGDATECONTRACTOR ||
        event.title === WorkStatus.JOININGDATEEMPLOYEE ||
        event.title === WorkStatus.JOININGDATETRAINEE
      ) {
        // eventElement.style.backgroundColor = '#1B84FF ' // Yellow
        eventElement.style.backgroundColor = '#1E88E5' // Very light amber

        // eventElement.style.color = 'black !important'
        eventElement.style.setProperty('color', 'white', 'important')

        eventElement.style.fontSize = '12px'
      }
      if (info.event.title === WorkStatus.WEEKLYOFF) {
        // Apply styles directly if necessary
        info.el.style.backgroundColor = 'rgb(207, 181, 140)' // Tomato color
        // info.el.style.backgroundColor = '#F4A261' //	Grayish Orange
        eventElement.style.setProperty('color', 'black', 'important')

        info.el.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'

        // return
      }
      if (event.title === WorkStatus.BREAK) {
        // Apply styles directly if necessary
        // eventElement.style.backgroundColor = 'orange'
        eventElement.style.backgroundColor = '#90A4AE' //Soft apricot

        eventElement.style.setProperty('color', 'black', 'important')

        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
      }

      if (
        event.title === WorkStatus.LEAVECANCELLED ||
        event.title === WorkStatus.LEAVEFIRSTHALFCANCELLED ||
        event.title === WorkStatus.LEAVESECONDHALFCANCELLED
      ) {
        return
      }

      if (event.title === 'Late') {
        // Apply styles directly if necessary
        eventElement.style.backgroundColor = '#FFA500' // Tomato color
        eventElement.style.color = 'white' // White text
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
        // borderColor: '#FF3B3B',
        // textColor: '#FFFFFF'
        // if (event.extendedProps['lateLogin'] !== null) {
        //   eventElement.setAttribute('title', `Late by ${event.extendedProps['lateLogin']} minutes`)
        // }
        eventElement.style.borderLeft = '4px solid #FF3B3B' // For Late
      }
      if (event.title === 'Early') {
        // Apply styles directly if necessary
        eventElement.style.backgroundColor = '#4ECDC4' // Tomato color
        // eventElement.style.color = 'white' // White text
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
        eventElement.style.setProperty('color', 'white', 'important')
        //     borderColor: '#38B2AC',
        // textColor: '#FFFFFF'
        // if (event.extendedProps['earlyLeave'] !== null) {
        //   eventElement.setAttribute(
        //     'title',
        //     `Early by ${event.extendedProps['earlyLeave']} minutes`
        //   )
        // }
        eventElement.style.borderLeft = '4px solid #38B2AC' // For Early
      }

      if (event.title === this.workStatusEnum.ABSENTHALFDUETOLATE) {
        // Apply styles directly if necessary
        eventElement.style.backgroundColor = '#FF6B6B' // Tomato color
        eventElement.style.color = 'white' // White text
        eventElement.style.border = 'none' // No border
        eventElement.style.fontSize = '12px'
        // borderColor: '#FF3B3B',
        // textColor: '#FFFFFF'

        eventElement.style.borderLeft = '4px solid #FF3B3B' // For Late
      }

      if (
        event.title === WorkStatus.LEAVECANCELREQUESTED ||
        event.title === WorkStatus.LEAVEFIRSTHALFCANCELREQUESTED ||
        event.title === WorkStatus.LEAVESECONDHALFCANCELREQUESTED
      ) {
        eventElement.style.backgroundColor = '#17a2b8'
        eventElement.style.color = 'black'
        eventElement.style.border = 'none'
        eventElement.style.fontSize = '12px'
        eventElement.style.borderLeft = '4px solid #FF9800'
      }

      if (
        // event.title === WorkStatus.LEAVECANCELAPPROVED ||
        event.title === WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED ||
        event.title === WorkStatus.LEAVESECONDHALFCANCELAPPROVED
      ) {
        eventElement.style.backgroundColor = '#17a2b8'
        eventElement.style.color = 'black'
        eventElement.style.border = 'none'
        eventElement.style.fontSize = '12px'
        eventElement.style.borderLeft = '4px solid #FF9800'
      }

      // Delete Icon for workStatus
      if (this.hasPermission({ calendarEvent: ['delete'] })) {
        const deleteBtn = document.createElement('span')

        deleteBtn.innerHTML = '<i class="ti ti-trash"></i>'

        deleteBtn.style.cssText = `
          position: absolute;
          right: 4px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          font-size: 11px;
          opacity: 0;
          transition: opacity 0.2s;
          z-index: 10;
        `

        info.el.style.position = 'relative'
        info.el.style.paddingRight = '18px'

        info.el.addEventListener('mouseenter', () => {
          deleteBtn.style.opacity = '1'
        })

        info.el.addEventListener('mouseleave', () => {
          deleteBtn.style.opacity = '0'
        })

        deleteBtn.addEventListener('click', (e: MouseEvent) => {
          e.stopPropagation()

          const eventId = info.event.id

          if (!eventId) {
            // console.warn('Event ID not found')
            return
          }

          const modalRef = this.modalService.open(DeleteComponent)

          modalRef.componentInstance.recordId = eventId

          modalRef.result.then(
            result => {
              if (result) {
                this.store.dispatch(deleteEvent({ id: result.id }))
              }
            },
            reason => {
              if (reason) {
                this.store.dispatch(deleteEvent({ id: reason.id }))
              }
            }
          )
        })

        info.el.appendChild(deleteBtn)
      }
    },
    // This option controls the number of events visible before showing the "More" link
    dayMaxEventRows: 3, // Show up to 3 events, then show "More"

    // Customize the "More" link text
    moreLinkText: 'More', // Customize text for the "More" link

    // Show a tooltip or popup when hovering over an event
    eventMouseEnter: info => {
      const event = info.event
      // ⛔ Don't show tooltip for 'Weekly Off'
      if (
        event.title === WorkStatus.WEEKLYOFF ||
        event.title === WorkStatus.ABSENT ||
        event.title === WorkStatus.PRESENT ||
        event.title === WorkStatus.REGULARIZEPRESENT ||
        event.title === WorkStatus.ABSENTFIRSTHALF ||
        event.title === WorkStatus.ABSENTSECONDHALF ||
        event.title === WorkStatus.LEAVECANCELLED ||
        event.title === WorkStatus.LEAVEREJECTED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTCANCELLED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTREJECTED ||
        event.title === WorkStatus.COMPOFFCANCELLED ||
        event.title === WorkStatus.COMPOFFLEAVECANCELLED ||
        event.title === WorkStatus.COMPOFFLEAVEREJECTED ||
        event.title === WorkStatus.COMPOFFREJECTED ||
        event.title === WorkStatus.LEAVESECONDHALFCANCELLED ||
        event.title === WorkStatus.LEAVESECONDHALFREJECTED ||
        event.title === WorkStatus.REGULARIZEPRESENTFIRSTHALF ||
        event.title === WorkStatus.REGULARIZEPRESENTSECONDHALF ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALFCANCELLED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALFREJECTED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALFCANCELLED ||
        event.title === WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALFREJECTED
      ) {
        return
      }
      // Create a custom popup (tooltip) on hover
      const tooltip = document.createElement('div')
      tooltip.classList.add('event-tooltip')
      if (event.title === 'Holiday') {
        tooltip.innerHTML = `
        <strong>Holiday </strong> 
         <br>
        
        ${event.extendedProps['description'] || 'N/A'}
      `
      } else {
        const clockInDisplay = event.extendedProps['clockInDisplay'] || 'N/A'
        const clockOutDisplay = event.extendedProps['clockOutDisplay'] || 'N/A'
        //   tooltip.innerHTML = `
        //   <strong>${event.title === WorkStatus.CLOCKIN ? 'Clock In/Out' : event.title}</strong><br>
        //   <strong>In:</strong> ${event.start?.toLocaleString()} ${
        //     event.extendedProps['autoClockIn'] ? '(Auto Clocked In)' : ''
        //   }<br>
        //   <strong>Out:</strong> ${event.end ? event.end.toLocaleString() : 'N/A'} ${
        //     event.extendedProps['autoClockOut'] ? '(Auto Clocked Out)' : ''
        //   }<br>
        //   <strong>Duration:</strong> ${event.extendedProps['duration'] || 'N/A'}
        // `
        tooltip.innerHTML = `
          <strong>${event.title === WorkStatus.CLOCKIN ? 'Clock In/Out' : event.title}</strong><br>
          <strong>In:</strong> ${clockInDisplay} ${
            event.extendedProps['autoClockIn'] ? '(Auto Clocked In)' : ''
          }<br>
          <strong>Out:</strong> ${clockOutDisplay} ${
            event.extendedProps['autoClockOut'] ? '(Auto Clocked Out)' : ''
          }<br>
          <strong>Duration:</strong> ${event.extendedProps['duration'] || 'N/A'}
        `
      }

      // Style the tooltip
      tooltip.style.position = 'absolute'
      tooltip.style.backgroundColor = '#F26522'
      tooltip.style.color = 'white'
      tooltip.style.padding = '10px'
      tooltip.style.borderRadius = '5px'
      tooltip.style.zIndex = '9999'
      tooltip.style.maxWidth = '200px'

      // Position the tooltip relative to the event element
      document.body.appendChild(tooltip)
      const eventElement = info.el
      const eventRect = eventElement.getBoundingClientRect()
      tooltip.style.left = `${eventRect.left + window.scrollX + 20}px`
      tooltip.style.top = `${eventRect.top + window.scrollY - tooltip.offsetHeight - 10}px`

      // Remove the tooltip when the mouse leaves the event
      info.el.addEventListener('mouseleave', () => {
        tooltip.remove()
      })
    },

    eventMouseLeave: info => {
      // Remove the tooltip when the mouse leaves the event
      const tooltip = document.querySelector('.event-tooltip')
      if (tooltip) {
        tooltip.remove()
      }
    },

    // eventContent: function (arg) {
    //   const title = arg.event.title
    //   const timeText = arg.timeText

    //   // Hide time text for Weekly Off or Holiday
    //   if (
    //     title === 'Weekly Off' ||
    //     title === 'Holiday' ||
    //     title === 'Absent' ||
    //     title === 'Leave' ||
    //     title === 'Leave First Half' ||
    //     title === 'Leave Second Half' ||
    //     title === 'Present' ||
    //     title === 'Date of Joining'
    //   ) {
    //     return { html: `<div class="fc-event-title">${title}</div>` }
    //   }

    //   if (
    //     title === 'Weekly Off' ||
    //     title === 'Holiday' ||
    //     title === 'Absent' ||
    //     title === 'Leave' ||
    //     title === 'Leave First Half' ||
    //     title === 'Leave Second Half' ||
    //     title === 'Present' ||
    //     title === 'Date of Joining' ||
    //     title ===
    //       `Leave (
    //       (LeaveType.LEAVEWITHOUTPAY ||
    //         LeaveType.BEREAVEMENT ||
    //         LeaveType.CASUAL ||
    //         LeaveType.COMPOFF ||
    //         LeaveType.FLOATER ||
    //         LeaveType.MARRIAGE ||
    //         LeaveType.MATERNITY ||
    //         LeaveType.PATERNITY ||
    //         LeaveType.PL ||
    //         LeaveType.RELIGIOUSFESTIVAL ||
    //         LeaveType.SABBATICAL ||
    //         LeaveType.SICK ||
    //         LeaveType.STUDY) )`
    //   ) {
    //     return { html: `<div class="fc-event-title">${title}</div>` }
    //   }

    //   // Default rendering for other events
    //   return {
    //     // html: `
    //     //   <div class="fc-event-time">${timeText}</div>
    //     //   <div class="fc-event-title">${title}</div>
    //     // `
    //     html: `
    //       <div class="fc-event-title">${title}</div>
    //     `
    //   }
    // }

    //10 dec 2025
    eventContent: (arg: any) => {
      const title = arg.event.title as string
      const leaveType = arg.event.extendedProps?.leaveType as string | undefined

      // Build label for leave with type
      const isFullDayLeave = title === 'Leave' && leaveType

      const isFirstHalfLeave =
        (title === 'Leave First Half' || title === 'Leave 1st Half') && leaveType

      const isSecondHalfLeave =
        (title === 'Leave Second Half' || title === 'Leave 2nd Half') && leaveType

      let displayTitle = title

      if (isFullDayLeave) {
        displayTitle = `Leave (${leaveType})`
      } else if (isFirstHalfLeave) {
        displayTitle = `Leave First Half (${leaveType})`
      } else if (isSecondHalfLeave) {
        displayTitle = `Leave Second Half (${leaveType})`
      }

      if (
        title === WorkStatus.WEEKLYOFF ||
        title === WorkStatus.HOLIDAY ||
        title === WorkStatus.ABSENT ||
        title === WorkStatus.PRESENT ||
        title === WorkStatus.JOININGDATE ||
        title === WorkStatus.PRESENTHALF ||
        title === WorkStatus.REGULARIZEPRESENT ||
        title === WorkStatus.REGULARIZEPRESENTHALF ||
        isFullDayLeave ||
        isFirstHalfLeave ||
        isSecondHalfLeave
      ) {
        return { html: `<div class="fc-event-title">${displayTitle}</div>` }
      }

      // default rendering
      //   return {
      //     html: `
      //   <div class="fc-event-time">${arg.timeText}</div>
      //   <div class="fc-event-title">${displayTitle}</div>
      // `
      //   }
      return {
        html: `
      <div class="fc-event-title">${displayTitle}</div>
    `
      }
    }
  }
  eventForm!: FormGroup
  title = ''
  workStatusEnum = WorkStatus
  attendanceOffCanvas = true
  constructor(
    private fb: FormBuilder,
    private actions$: Actions,
    private offcanvasService: NgbOffcanvas,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal
  ) {
    // this.store.dispatch(loadBusinesses({ params: null }))
    this.store.dispatch(clearAttendanceState())
    this.attendances$ = this.store.select(selectClockinclockouts)
    this.countries$ = this.store.select(selectAllCountries)
    this.store.select(selectAllCountries).subscribe(countries => {
      this.timezoneOptions = countries.flatMap(country =>
        (country.timezones || []).map(tz => ({
          label: `${country.name} - ${tz.gmtOffsetName} (${tz.zoneName})`,
          zoneName: tz.zoneName,
          countryId: country.id,
          countryName: country.name
        }))
      )
      // set default timezone AFTER timezoneOptions exist
      let systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (TIMEZONE_ALIASES[systemTimeZone]) {
        systemTimeZone = TIMEZONE_ALIASES[systemTimeZone]
      }

      // ensure the timezone exists in options
      const exists = this.timezoneOptions.some(tz => tz.zoneName === systemTimeZone)
      this.selectedZoneName = exists ? systemTimeZone : ''

      // console.log('Selected timezone:', systemTimeZone)
      // 👇 set system timezone AFTER options are built
      this.setSystemTimezone()

      // const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      // console.log('time zone', timezone)
    })
    // const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    // console.log('time zone', timezone)
    this.loadAllBusinesses() // Load all businesses initially

    this.store
      .select(selectUser)
      .pipe(
        take(1),
        tap(user => {
          this.user = user
          if (user) {
            this.selectedBusinessId = user.businessId
            this.onBusinessChange(this.selectedBusinessId)
            this.selectedEmployeeId = user.id
            this.employeeId = user.id
            const joiningDateRaw = (user as any).joiningDate
            if (joiningDateRaw) {
              this.employeeJoiningDate =
                typeof joiningDateRaw === 'string' ? joiningDateRaw : joiningDateRaw.toISOString()
              this.minDateTime = this.formatForDateTimeLocal(new Date(this.employeeJoiningDate))
            }
            this.validRangeSet = false
          }

          const businessIds = user?.businessIds // your array of business IDs
          if (businessIds && businessIds.length) {
            this.businessIdss = businessIds
            // const paramString = `businessIds=${businessIds.join(',')}`
            // this.store.dispatch(loadBusinesses({ params: paramString }))
          }
          this.loadAllBusinesses() // Load all businesses initially
          const now = new Date()
          const start = new Date(now.getFullYear(), now.getMonth(), 1)
          const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
          // this.loadAttendances(start, end)
          this.loadAssignedLeavePolicy(user?.id)
        })
      )
      .subscribe()

    // this.attendances$.subscribe(data => {
    //   this.calendarOptions.events = this.mapAttendancesToEvents(data) // ✅ use attendances directly
    // })

    // this.attendances$.subscribe(data => {
    //   $('#dateAccordion').html('')
    //   $('#dateCompoffAccordion').html('')
    //   // this.dates.clear()

    //   if (this.form && this.form.get('dates') instanceof FormArray) {
    //     this.dates.clear()
    //   }
    //   const allDayRecords = data.flatMap(item => item.dayRecords || [])
    //   const absentRecords = allDayRecords.filter(record => record.workStatus === 'Absent')

    //   const clockInDates = absentRecords
    //     .map(record => dayjs(record.clockIn))
    //     .sort((a, b) => a.unix() - b.unix())

    //   const defaultStartDate = clockInDates[0]
    //   const defaultEndDate = clockInDates[clockInDates.length - 1]

    //   // Set calendar events
    //   this.calendarOptions.events = this.mapAttendancesToEvents(allDayRecords)

    //   if (!this.user || absentRecords.length === 0) return

    //   this.attendancePolicyService
    //     .assignedPolicyByUserId(this.selectedEmployeeId ?? this.user.id)
    //     .subscribe(policy => {
    //       const startTime = policy?.startTime ?? '09:00'
    //       const endTime = policy?.endTime ?? '18:00'

    //       absentRecords.forEach(record => {
    //         const currentDateStr = dayjs(record.clockIn).format('DD/MM/YYYY')
    //         const dateInputFormat = dayjs(record.clockIn).format('YYYY-MM-DD')
    //         this.addAccordionItem(
    //           currentDateStr,
    //           dateInputFormat,
    //           startTime,
    //           endTime,
    //           'dateAccordion'
    //         )
    //       })

    //       // Optionally initialize daterangepicker using defaultStartDate/defaultEndDate
    //       setTimeout(() => {
    //         $('#dateRangeInputModal').daterangepicker({
    //           autoUpdateInput: true,
    //           startDate: defaultStartDate?.toDate(),
    //           endDate: defaultEndDate?.toDate(),
    //           minDate: dayjs(this.employeeJoiningDate).toDate(), // ✅ Disables dates before joining
    //           maxDate: dayjs().toDate(), // ⛔ Disables future dates
    //           locale: {
    //             format: 'DD/MM/YYYY',
    //             cancelLabel: 'Clear'
    //           }
    //         })
    //       }, 0)
    //     })
    // })

    //4 july 2025
    combineLatest([this.attendances$, this.store.select(selectTodaysClockinoutRecords)])
      // this.attendances$
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        // filter(data => data !== null && data !== undefined),
        filter(([data]) => data !== null && data !== undefined),
        takeUntil(this.destroyed$)
      )
      // .subscribe(data => {
      .subscribe(([data, todayRecords]) => {
        // 🧹 Clear previous accordion content
        $('#dateAccordion').html('')
        $('#dateCompoffAccordion').html('')

        // ✅ Safely clear FormArray
        if (this.form?.get('dates') instanceof FormArray) {
          this.dates.clear()
        }

        // 🔄 Flatten and filter records
        // const allDayRecords = data.flatMap(item => item.dayRecords || [])
        // const allDayRecords = [
        //   ...data.flatMap((item: any) => (item?.dayRecords?.length ? item.dayRecords : [item])),
        //   ...(todayRecords || [])
        // ]
        const allDayRecords = [
          ...data.flatMap((item: any) => (item?.dayRecords?.length ? item.dayRecords : [item])),
          ...(todayRecords || []).filter(
            (t: any) =>
              !data.some(
                (d: any) => d.id === t.id || (d.dayRecords || []).some((r: any) => r.id === t.id)
              )
          )
        ]
        // const absentRecords = allDayRecords.filter(r => r.workStatus === 'Absent')
        const absentRecords = allDayRecords
          .filter(r =>
            ['Absent', 'Absent for Half Day', 'Absent First Half', 'Absent Second Half'].includes(
              r.workStatus
            )
          )
          .filter(r => this.isOnOrAfterJoiningDate(r.clockIn))

        // 📆 Sort absent records for date range
        const clockInDates = absentRecords
          .map(record => dayjs(record.clockIn))
          .sort((a, b) => a.unix() - b.unix())

        const defaultStartDate = clockInDates[0]?.toDate()
        const defaultEndDate = clockInDates[clockInDates.length - 1]?.toDate()

        // ✅ Generate events
        const events = this.mapAttendancesToEvents(allDayRecords)

        // ✅ Set events using FullCalendar API
        const calendarApi = this.calendarComponent?.getApi?.()
        if (calendarApi) {
          calendarApi.removeAllEvents()
          calendarApi.addEventSource(events)

          // if (this.employeeJoiningDate) {
          //   calendarApi.setOption('validRange', {
          //     start: this.employeeJoiningDate
          //   })
          // }
          if (this.employeeJoiningDate && !this.validRangeSet) {
            calendarApi.setOption('validRange', {
              start: this.employeeJoiningDate
            })
            this.validRangeSet = true
            // calendarApi.gotoDate(this.employeeJoiningDate) // optional: jump to the month
          }
        } else {
          // fallback for initial load if calendar hasn't initialized
          this.calendarOptions.events = events
        }

        if (!this.user || absentRecords.length === 0) return
        this.attendanceOffCanvas = !this.user || absentRecords.length === 0
        // 🎯 Get user's assigned policy
        this.attendancePolicyService
          .assignedPolicyByUserId(this.selectedEmployeeId)
          .subscribe(policy => {
            if (!policy || !policy.length || !policy[0].attendancePolicy) {
              // console.error('No attendance policy assigned to this user.')
              return
            }
            const startTime = policy[0].attendancePolicy?.shiftStartTime ?? '09:00'
            const endTime = policy[0].attendancePolicy?.shiftEndTime ?? '18:00'
            // console.log('policy', policy[0].attendancePolicy)
            // ➕ Add accordion items for each absent date
            absentRecords.forEach(record => {
              const currentDate = dayjs(record.clockIn)
                .tz(this.selectedZoneName)
                .format('YYYY-MM-DD')
              // ❌ Skip if regularize request exists for the same date
              // const hasRegularizeRequest = allDayRecords.some(
              //   r =>
              //     r.workStatus === WorkStatus.ATTENDANCEREGULARIZEREQUEST &&
              //     dayjs(r.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD') === currentDate
              // )

              const hasRegularizeRequest = allDayRecords.some(
                r =>
                  (r.workStatus === WorkStatus.ATTENDANCEREGULARIZEREQUEST ||
                    r.workStatus === WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALF ||
                    r.workStatus === WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALF) &&
                  dayjs(r.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD') === currentDate
              )

              if (hasRegularizeRequest) return // 🛑 Skip this date
              const currentDateStr = dayjs(record.clockIn).format('DD/MM/YYYY')
              const dateInputFormat = dayjs(record.clockIn).format('YYYY-MM-DD')
              this.addAccordionItem(
                currentDateStr,
                dateInputFormat,
                startTime,
                endTime,
                'dateAccordion',
                record.workStatus
              )
            })

            // 🗓️ Initialize daterangepicker safely
            setTimeout(() => {
              $('#dateRangeInputModal').daterangepicker({
                autoUpdateInput: true,
                startDate: defaultStartDate,
                endDate: defaultEndDate,
                minDate: dayjs(this.employeeJoiningDate).toDate(),
                maxDate: dayjs().toDate(),
                locale: {
                  format: 'DD/MM/YYYY',
                  cancelLabel: 'Clear'
                }
              })
              $('#dateRangeInputCompoffModal').daterangepicker({
                autoUpdateInput: true,
                // startDate: defaultStartDate,
                // endDate: defaultEndDate,
                minDate: dayjs(this.employeeJoiningDate).toDate(),
                maxDate: dayjs().toDate(),
                locale: {
                  format: 'DD/MM/YYYY',
                  cancelLabel: 'Clear'
                }
              })
              // const config = this.getDateRangePickerConfig(defaultStartDate, defaultEndDate)
              // $('#dateRangeInputCompoffModal').daterangepicker(config)
              // $('#dateRangeInputCompoffModal').daterangepicker(config)
            }, 0)
            // console.log('min date 1', dayjs(this.employeeJoiningDate).toDate())
          })
      })
  }

  ngOnInit() {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')

    this.maxDateTime = `${year}-${month}-${day}T${hours}:${minutes}`
    this.form = this.fb.group({
      dates: this.fb.array([]) // dynamic list of dates
    })

    this.employees$.pipe(takeUntil(this.destroyed$)).subscribe(employees => {
      this.employeesCount = employees?.length ?? 0
    })

    this.route.queryParams
      .pipe(
        filter(params => !!params['userId'] && !!params['businessId']),
        take(1)
      )
      .subscribe(params => {
        this.selectedUserId = params['userId']
        this.selectedBusinessId = params['businessId']
        this.selectedDate = params['date']

        this.onBusinessChange(this.selectedBusinessId)

        this.employees$
          .pipe(
            filter(employees => employees.length > 0),
            take(1)
          )
          .subscribe(() => {
            this.selectedEmployeeId = this.selectedUserId
            this.employeeId = this.selectedUserId

            this.employees$.pipe(take(1)).subscribe(employees => {
              const emp = employees.find(e => e.id === this.selectedUserId)
              if (emp) {
                this.employeeJoiningDate =
                  typeof emp.joiningDate === 'string'
                    ? emp.joiningDate
                    : emp.joiningDate.toISOString()
                this.minDateTime = this.formatForDateTimeLocal(new Date(this.employeeJoiningDate))
              }
            })

            this.calanderRerenderToDate(this.selectedDate)
            this.loadAssignedLeavePolicy(this.selectedUserId)
          })
      })

    this.actions$
      .pipe(
        ofType(createEmployeeRequestsSuccess, createEmployeeRequestFailure),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        // 🧹 Clear previous accordion content
        $('#dateAccordion').html('')
        $('#dateCompoffAccordion').html('')

        // ✅ Safely clear FormArray
        if (this.form?.get('dates') instanceof FormArray) {
          this.dates.clear()
        }
        this.form.reset()
        this.closeOffcanvas()
        this.closeAllOffcanvas()
        this.calanderRerender()
        // if (this.offcanvasRef) {
        //   this.offcanvasRef.close() // 👈 Close the offcanvas
        // }
        // this.modalService.dismissAll() // or modalRef.close() if you're using one
        // this.loadRequests()
      })

    this.rangeForm = new FormGroup({
      daterange: new FormControl(null)
    })

    this.businesses$.subscribe(businesses => {
      if (businesses.length === 1) {
        this.form.get('businessId')?.setValue(businesses[0].id)
        // this.selectedBusinessId = businesses[0].id
        this.selectedBusinessId = businesses[0].id
        // this.onBusinessChange(businesses[0].id)
        this.onBusinessChange(this.selectedBusinessId)
      }
    })

    this.employees$.subscribe(employees => {
      this.employeesCount = employees?.length ?? 0
      if (employees.length === 1) {
        // this.form.get('businessId')?.setValue(businesses[0].id)
        // this.selectedBusinessId = businesses[0].id
        this.selectedEmployeeId = employees[0].id
        // this.onBusinessChange(businesses[0].id)
        this.onEmployeeChange(this.selectedEmployeeId)
      }
      if (employees.length > 1 && this.user?.id) {
        const loggedInEmployee = employees.find(e => e.id === this.user?.id)
        if (loggedInEmployee && !this.selectedEmployeeId) {
          this.selectedEmployeeId = this.user.id
        }
      }
    })

    this.store.dispatch(loadCountries())

    // this.store.select(selectAllCountries).subscribe(countries => {
    //   this.countries = countries
    //   // Optional: auto-select first
    //   if (!this.selectedCountryId && countries.length > 0) {
    //     this.selectedCountryId = countries[0].id
    //     this.onCountryChange(this.selectedCountryId)
    //   }
    // })
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    this.selectedZoneName = localTimezone

    // Optional: update calendar if already initialized
    this.calendarOptions.timeZone = localTimezone
    // console.log('zone', this.selectedZoneName)

    this.eventForm = this.fb.group({
      workStatus: ['', [Validators.required, Validators.minLength(2), noLeadingTrailingSpaces()]], // Example validators
      userId: [''],
      lateLogin: ['', [this.durationHmValidator()]],
      // clockIn: ['', Validators.required]
      // clockIn: [
      //   '',
      //   [
      //     (control: AbstractControl): ValidationErrors | null => {
      //       const val = control.value
      //       if (!val) return null
      //       return new Date(val) > new Date() ? { maxDateTime: true } : null
      //     }
      //   ]
      // ],
      clockIn: [
        '',
        [
          (control: AbstractControl): ValidationErrors | null => {
            const val = control.value
            if (!val) return null

            //  Skip future-date check for Holiday and Weekly Off
            const status = this.eventForm?.get('workStatus')?.value
            if (
              status === this.workStatusEnum.HOLIDAY ||
              status === this.workStatusEnum.WEEKLYOFF
            ) {
              return null
            }

            return new Date(val) > new Date() ? { maxDateTime: true } : null
          }
        ]
      ],
      clockOut: ['']
      // businessOwnerId: ['']
    })
    this.eventForm.get('workStatus')!.valueChanges.subscribe(status => {
      const clockIn = this.eventForm.get('clockIn')!
      const lateLogin = this.eventForm.get('lateLogin')!

      if (status === this.workStatusEnum.ABSENTHALFDUETOLATE) {
        // Absent for late: only "Absent On" required
        lateLogin.reset()
        lateLogin.clearValidators()

        clockIn.setValidators([Validators.required])
      } else if (status === this.workStatusEnum.LATE || status === this.workStatusEnum.EARLY) {
        // Late / Early: time + minutes required
        clockIn.setValidators([Validators.required])
        lateLogin.setValidators([
          Validators.required,
          Validators.min(0),
          this.durationHmValidator()
        ])
      } else {
        // nothing selected / other
        clockIn.clearValidators()
        lateLogin.clearValidators()
      }

      clockIn.updateValueAndValidity()
      lateLogin.updateValueAndValidity()
    })

    this.actions$
      .pipe(
        ofType(createEventSuccess, createEventFailure),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.eventForm.reset({
          status: 'Pending', // 👈 preserve the default value
          statusReason: '',
          businessId: '',
          userId: '',
          leavePolicyId: ''
        })

        this.modalService.dismissAll() // or modalRef.close() if you're using one
        this.calanderRerender()
      })
    this.attendances$?.pipe(takeUntil(this.destroyed$)).subscribe(data => {
      if (!data) return
      this.checkRegularizeEligibility(data)
    })

    this.eventForm.get('workStatus')?.valueChanges.subscribe(value => {
      const selectedEvent = this.eventTypes.find(event => event.value === value)
      this.selectedEventLabel = selectedEvent?.label || 'Event'
    })

    combineLatest([
      this.eventForm.get('clockIn')!.valueChanges,
      this.eventForm.get('clockOut')!.valueChanges
    ]).subscribe(([clockIn, clockOut]) => {
      if (clockIn && clockOut) {
        const start = new Date(clockIn)
        const end = new Date(clockOut)
        const diffMs = end.getTime() - start.getTime()

        if (diffMs >= 0) {
          const totalMinutes = Math.floor(diffMs / 60000)
          const hours = Math.floor(totalMinutes / 60)
          const minutes = totalMinutes % 60

          const duration = `${hours}h ${minutes}m`
          // if (hours > 0) duration += `${hours}h `
          // if (minutes > 0) duration += `${minutes}m`
          // duration = duration.trim()

          this.eventForm.get('lateLogin')!.setValue(duration, { emitEvent: false })
        } else {
          this.eventForm.get('lateLogin')!.setValue('', { emitEvent: false })
        }
      }
    })

    this.actions$
      .pipe(ofType(deleteEventSuccess, deleteEventFailure), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.calanderRerender()
      })

    this.store
      .select(selectUserPermissions)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(perms => {
        this.userPermissions = this.normalizePermissions(perms)
      })
  }

  get dates(): FormArray {
    return (this.form.get('dates') as FormArray) ?? []
  }

  private normalizePermissions(perms: any): Record<string, string[]> {
    const map: Record<string, string[]> = {}

    if (!perms) return map

    if (Array.isArray(perms)) {
      perms.forEach((p: any) => {
        if (p?.moduleName) {
          map[p.moduleName] = p.permissions || []
        }
      })
    } else {
      Object.assign(map, perms)
    }

    return map
  }

  loadAttendances(start: Date, end: Date) {
    // ✅ Clear previous records from state
    // this.store.dispatch(clearAttendanceState())
    this.store.dispatch(clearAttendanceState())
    const params: any = {}
    params.userId = this.user?.id
    params.timezone = this.selectedZoneName || Intl.DateTimeFormat().resolvedOptions().timeZone

    // if (this.leaveType ) {
    //   params.leaveType = this.leaveType
    // }
    // if (this.status) {
    //   params.workStatus = this.status
    // }
    // if (this.dateRange?.startDate && this.dateRange?.endDate) {
    //   params.fromDate = this.dateRange.startDate.format('YYYY-MM-DD')
    //   params.toDate = this.dateRange.endDate.format('YYYY-MM-DD')
    // }
    // const fromDate = dateInfo.startStr // ISO format, e.g. '2025-07-01'
    // const toDate = dateInfo.endStr // ISO format, e.g. '2025-08-01'
    params.fromDate = start.toISOString().split('T')[0]
    params.toDate = end.toISOString().split('T')[0]
    // const employeeId = this.employeeId || this.user?.id
    const employeeId = this.employeeId || this.selectedUserId || this.user?.id
    if (!employeeId) return
    this.store.dispatch(
      loadEmployeeAttendance({
        // id: this.employeeId ?? this.user?.id,
        id: employeeId,
        // params: null
        params: new URLSearchParams(params).toString()
      })
    )
  }

  // private mapAttendancesToEvents(attendances: ClockInClockOut[]) {
  //   if (!attendances) return []

  //   // 👉 Extract joining date from attendance
  //   const joiningRecord = attendances.find(record => record.workStatus === 'Date of Joining')

  //   this.employeeJoiningDate = joiningRecord?.clockIn
  //     ? new Date(joiningRecord.clockIn).toISOString().slice(0, 10)
  //     : ''
  //   const calendarApi = this.calendarComponent.getApi()
  //   calendarApi.setOption('validRange', { start: this.employeeJoiningDate })
  //   return attendances.map(record => {
  //     // let backgroundColor = ''

  //     // if (record.workStatus === 'Present' || record.workStatus === 'Late Login') {
  //     //   backgroundColor = 'rgb(253, 216, 53)' // yellow
  //     // }

  //     const statusColorMap: Record<string, string> = {
  //       Present: '#4CAF50', // Green
  //       Absent: '#F44336', // Red
  //       Leave: '#FF9800', // Orange
  //       Holiday: '#9C27B0', // Purple
  //       ClockIn: '#03A9F4', // Light Blue
  //       ClockOut: '#FFC107', // Amber
  //       WeeklyOff: '#795548', // Brown
  //       LeaveFirstHalf: '#FF9800', // Orange
  //       LeaveSecondHalf: '#FF9800', // Orange
  //       DateofJoining: '#1B84FF'
  //     }

  //     const statusPriorityMap: Record<string, number> = {
  //       'Date of Joining': 1,
  //       Present: 1,
  //       'Late Login': 1,
  //       Leave: 2,
  //       'Leave First Half': 2,
  //       'Leave Second Half': 2,
  //       Holiday: 2,
  //       Absent: 2,
  //       'Weekly Off': 3,
  //       ClockIn: 4,
  //       ClockOut: 4,
  //       Break: 4
  //     }

  //     return {
  //       title: record.workStatus,
  //       start: record.clockIn,
  //       end: record.clockOut,
  //       // allDay: true,
  //       backgroundColor: statusColorMap[record.workStatus] || '#607D8B', // default
  //       // backgroundColor,
  //       textColor: 'white',
  //       extendedProps: {
  //         description: record.holiday?.title || '',
  //         title: record.workStatus,
  //         orderPriority: statusPriorityMap[record.workStatus] ?? 5 // fallback priority
  //       }
  //     }
  //   })
  // }

  //4 july 2025
  // private mapAttendancesToEvents(attendances: ClockInClockOut[]) {
  //   if (!attendances) return []

  //   const joiningRecord = attendances.find(record => record.workStatus === 'Date of Joining')
  //   // this.employeeJoiningDate = joiningRecord?.clockIn
  //   //   ? new Date(joiningRecord.clockIn).toISOString().slice(0, 10)
  //   //   : ''
  //   this.employeeJoiningDate = joiningRecord?.clockIn
  //     ? dayjs.utc(joiningRecord.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD')
  //     : ''

  //   const statusColorMap: Record<string, string> = {
  //     Present: '#4CAF50',
  //     Absent: '#F44336',
  //     Leave: '#FF9800',
  //     Holiday: '#9C27B0',
  //     ClockIn: '#03A9F4',
  //     ClockOut: '#FFC107',
  //     WeeklyOff: '#795548',
  //     LeaveFirstHalf: '#FF9800',
  //     LeaveSecondHalf: '#FF9800',
  //     DateofJoining: '#1B84FF'
  //   }

  //   const statusPriorityMap: Record<string, number> = {
  //     'Date of Joining': 1,
  //     Present: 1,
  //     'Late Login': 1,
  //     Leave: 2,
  //     'Leave First Half': 2,
  //     'Leave Second Half': 2,
  //     Holiday: 2,
  //     Absent: 2,
  //     'Weekly Off': 3,
  //     ClockIn: 4,
  //     ClockOut: 4,
  //     Break: 4
  //   }
  //   return attendances.map(record => {
  //     const clockIn = record.clockIn
  //       ? dayjs
  //           .utc(record.clockIn)
  //           .tz(this.selectedZoneName)
  //           .subtract(5.5, 'hour') // subtract timezone offset manually
  //           .format()
  //       : undefined

  //     // const clockIn = record.clockIn
  //     //   ? dayjs.utc(record.clockIn).tz(this.selectedZoneName).format()
  //     //   : undefined
  //     // const clockIn = record.clockIn
  //     //   ? dayjs(record.clockIn).tz(this.selectedZoneName).format()
  //     //   : undefined

  //     // const clockOut = record.clockOut
  //     //   ? dayjs.utc(record.clockOut).tz(this.selectedZoneName).format()
  //     //   : undefined
  //     // const clockOut = record.clockOut
  //     //   ? dayjs(record.clockOut).tz(this.selectedZoneName).format()
  //     //   : undefined
  //     const clockOut = record.clockOut
  //       ? dayjs
  //           .utc(record.clockOut)
  //           .tz(this.selectedZoneName)
  //           .subtract(5.5, 'hour') // subtract timezone offset manually
  //           .format()
  //       : undefined

  //     return {
  //       // title: record.workStatus,
  //       title: record.workStatus,
  //       start: clockIn,
  //       end: clockOut,
  //       backgroundColor: statusColorMap[record.workStatus] || '#607D8B',
  //       textColor: 'white',
  //       extendedProps: {
  //         description: record.holiday?.title || '',
  //         title: record.workStatus,
  //         orderPriority: statusPriorityMap[record.workStatus] ?? 5
  //       }
  //     }
  //   })
  // }

  //14 july 2025
  // private mapAttendancesToEvents(attendances: ClockInClockOut[]) {
  //   if (!attendances) return []

  //   const joiningRecord = attendances.find(record => record.workStatus === 'Date of Joining')
  //   this.employeeJoiningDate = joiningRecord?.clockIn
  //     ? dayjs.utc(joiningRecord.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD')
  //     : ''

  //   const statusColorMap: Record<string, string> = {
  //     Present: '#4CAF50',
  //     Absent: '#F44336',
  //     Leave: '#FF9800',
  //     Holiday: '#9C27B0',
  //     ClockIn: '#03A9F4',
  //     ClockOut: '#FFC107',
  //     WeeklyOff: '#795548',
  //     LeaveFirstHalf: '#FF9800',
  //     LeaveSecondHalf: '#FF9800',
  //     DateofJoining: '#1B84FF',
  //     Break: '#607D8B'
  //   }

  //   // First sort everything by clockIn time
  //   const highPriorityStatuses = [
  //     WorkStatus.JOININGDATE,
  //     WorkStatus.PRESENT,
  //     WorkStatus.REGULARIZEPRESENT,
  //     WorkStatus.HOLIDAY,
  //     WorkStatus.ABSENT,
  //     WorkStatus.WEEKLYOFF,
  //     WorkStatus.LEAVE,
  //     WorkStatus.LEAVEFIRSTHALFAPPLIED,
  //     WorkStatus.LEAVEFIRSTHALF,
  //     WorkStatus.LEAVEFIRSTHALFREJECTED,
  //     WorkStatus.LEAVESECONDHALFAPPLIED,
  //     WorkStatus.LEAVESECONDHALF,
  //     WorkStatus.LEAVESECONDHALFREJECTED
  //   ]

  //   // const sortedRecords = [...attendances].sort((a, b) => {
  //   //   const isAPriority = highPriorityStatuses.includes(a.workStatus) ? -1 : 1
  //   //   const isBPriority = highPriorityStatuses.includes(b.workStatus) ? -1 : 1

  //   //   if (isAPriority !== isBPriority) {
  //   //     return isAPriority - isBPriority
  //   //   }

  //   //   const timeA = new Date(a.clockIn || a.clockIn).getTime()
  //   //   const timeB = new Date(b.clockIn || b.clockIn).getTime()
  //   //   return timeA - timeB
  //   // })

  //   const sortedRecords = [...attendances].sort((a, b) => {
  //     const aIsHighPriority = highPriorityStatuses.includes(a.workStatus as WorkStatus)
  //     const bIsHighPriority = highPriorityStatuses.includes(b.workStatus as WorkStatus)

  //     // Priority sort: high-priority status comes first
  //     if (aIsHighPriority !== bIsHighPriority) {
  //       return aIsHighPriority ? -1 : 1
  //     }

  //     // If priority is the same, sort by clockIn time
  //     const timeA = new Date(a.clockIn).getTime()
  //     const timeB = new Date(b.clockIn).getTime()
  //     return timeA - timeB
  //   })

  //   return sortedRecords.map(record => {
  //     // const clockIn = record.clockIn
  //     //   ? dayjs.utc(record.clockIn).tz(this.selectedZoneName).format()
  //     //   : undefined

  //     // const clockOut = record.clockOut
  //     //   ? dayjs.utc(record.clockOut).tz(this.selectedZoneName).format()
  //     //   : undefined
  //     const clockIn = record.clockIn
  //       ? dayjs
  //           .utc(record.clockIn)
  //           .tz(this.selectedZoneName)
  //           .subtract(5.5, 'hour') // subtract timezone offset manually
  //           .format()
  //       : undefined
  //     const clockOut = record.clockOut
  //       ? dayjs
  //           .utc(record.clockOut)
  //           .tz(this.selectedZoneName)
  //           .subtract(5.5, 'hour') // subtract timezone offset manually
  //           .format()
  //       : undefined

  //     return {
  //       title: record.workStatus === WorkStatus.CLOCKIN ? 'Clock In/Out' : record.workStatus,
  //       start: clockIn,
  //       end: clockOut,
  //       backgroundColor: statusColorMap[record.workStatus] || '#607D8B',
  //       textColor: 'white',
  //       extendedProps: {
  //         description: record.holiday?.title || '',
  //         duration: record.workHour || '',
  //         title: record.workStatus
  //       }
  //     }
  //   })
  // }

  //4 aug 2025
  // private mapAttendancesToEvents(attendances: ClockInClockOut[]) {
  //   // if (!attendances) return []
  //   if (!attendances || attendances.length === 0) return []

  //   // const joiningRecord = attendances.find(record => record.workStatus === WorkStatus.JOININGDATE)
  //   // this.employeeJoiningDate = joiningRecord?.clockIn
  //   //   ? dayjs.utc(joiningRecord.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD')
  //   //   : ''

  //   if (this.visibleRangeStart && this.visibleRangeEnd) {
  //     const start = this.visibleRangeStart.getTime()
  //     const end = this.visibleRangeEnd.getTime()
  //     attendances = attendances.filter(r => {
  //       const t = new Date(r.clockIn).getTime()
  //       return t >= start && t <= end
  //     })
  //   }

  //   const statusColorMap: Record<string, string> = {
  //     Present: '#4CAF50',
  //     Absent: '#F44336',
  //     Leave: '#FF9800',
  //     Holiday: '#9C27B0',
  //     ClockIn: '#03A9F4',
  //     ClockOut: '#FFC107',
  //     WeeklyOff: '#795548',
  //     LeaveFirstHalf: '#FF9800',
  //     LeaveSecondHalf: '#FF9800',
  //     DateofJoining: '#1B84FF',
  //     Break: '#607D8B',
  //     Compoff: '#8BC34A',
  //     RegularizePresent: '#2196F3',
  //     RegularizeRequest: '#03A9F4',
  //     LeaveRequest: '#FF9800',
  //     CompoffRequest: '#CDDC39'
  //   }

  //   const highPriorityStatuses = [
  //     WorkStatus.JOININGDATE,
  //     WorkStatus.JOININGDATETRAINEE,
  //     WorkStatus.JOININGDATEEMPLOYEE,
  //     WorkStatus.JOININGDATECONTRACTOR,
  //     WorkStatus.PRESENT,
  //     WorkStatus.REGULARIZEPRESENT,
  //     WorkStatus.REGULARIZEPRESENTHALF,
  //     WorkStatus.HOLIDAY,
  //     WorkStatus.LEAVE,
  //     WorkStatus.ABSENT,
  //     WorkStatus.WEEKLYOFF,
  //     WorkStatus.LEAVEFIRSTHALF,
  //     WorkStatus.LEAVESECONDHALF,
  //     WorkStatus.LEAVEFIRSTHALFAPPLIED,
  //     WorkStatus.LEAVEFIRSTHALFREJECTED,
  //     WorkStatus.LEAVESECONDHALFAPPLIED,
  //     WorkStatus.LEAVESECONDHALFREJECTED,
  //     WorkStatus.COMPOFFEARNED,
  //     WorkStatus.ATTENDANCEREGULARIZEREQUEST,
  //     WorkStatus.LEAVEREQUEST,
  //     WorkStatus.COMPOFFAPPLIED,
  //     WorkStatus.ABSENTFIRSTHALF,
  //     WorkStatus.ABSENTSECONDHALF,
  //     WorkStatus.ABSENTHALF
  //   ]

  //   // 🟡 Map of request-type statuses and their override equivalents
  //   const statusOverrideMap: Record<string, string[]> = {
  //     [WorkStatus.ATTENDANCEREGULARIZEREQUEST]: [
  //       WorkStatus.PRESENT,
  //       WorkStatus.REGULARIZEPRESENT,
  //       WorkStatus.REGULARIZEPRESENTHALF,
  //       WorkStatus.ATTENDANCEREGULARIZEREQUESTCANCELLED,
  //       WorkStatus.ATTENDANCEREGULARIZEREQUESTREJECTED,
  //       WorkStatus.WEEKLYOFF
  //     ],
  //     [WorkStatus.LEAVEREQUEST]: [
  //       WorkStatus.LEAVE,
  //       WorkStatus.LEAVECANCELLED,
  //       WorkStatus.LEAVEREJECTED,
  //       WorkStatus.LEAVEFIRSTHALF,
  //       WorkStatus.LEAVEFIRSTHALFCANCELLED,
  //       WorkStatus.LEAVEFIRSTHALFREJECTED,
  //       WorkStatus.LEAVESECONDHALF,
  //       WorkStatus.LEAVESECONDHALFCANCELLED,
  //       WorkStatus.LEAVESECONDHALFREJECTED
  //     ],
  //     [WorkStatus.LEAVEFIRSTHALFAPPLIED]: [
  //       WorkStatus.LEAVEFIRSTHALF,
  //       WorkStatus.LEAVEFIRSTHALFCANCELLED,
  //       WorkStatus.LEAVEFIRSTHALFREJECTED,
  //       WorkStatus.LEAVECANCELLED,
  //       WorkStatus.LEAVEREJECTED
  //     ],
  //     [WorkStatus.LEAVESECONDHALFAPPLIED]: [
  //       WorkStatus.LEAVESECONDHALF,
  //       WorkStatus.LEAVESECONDHALFCANCELLED,
  //       WorkStatus.LEAVESECONDHALFREJECTED,
  //       WorkStatus.LEAVECANCELLED,
  //       WorkStatus.LEAVEREJECTED
  //     ],
  //     [WorkStatus.COMPOFFAPPLIED]: [
  //       WorkStatus.COMPOFFEARNED,
  //       WorkStatus.COMPOFFCANCELLED,
  //       WorkStatus.COMPOFFREJECTED
  //     ],
  //     [WorkStatus.COMPOFFLEAVEAPPLIED]: [
  //       WorkStatus.COMPOFFLEAVEAPPROVE,
  //       WorkStatus.COMPOFFCANCELLED,
  //       WorkStatus.COMPOFFLEAVEREJECTED
  //     ]
  //     // ✅ Leave overrides Absent
  //     // [WorkStatus.LEAVE]: [WorkStatus.LEAVEREJECTED, WorkStatus.LEAVECANCELLED],
  //     // [WorkStatus.ABSENT]: [
  //     //   WorkStatus.PRESENT,
  //     //   WorkStatus.REGULARIZEPRESENT,
  //     //   WorkStatus.REGULARIZEPRESENTHALF,
  //     //   WorkStatus.LEAVE
  //     // ]

  //     // [WorkStatus.LEAVEFIRSTHALF]: [WorkStatus.ABSENT],
  //     // [WorkStatus.LEAVESECONDHALF]: [WorkStatus.ABSENT],
  //   }

  //   const cancelledStatuses: WorkStatus[] = [
  //     WorkStatus.LEAVECANCELLED,
  //     WorkStatus.LEAVEFIRSTHALFCANCELLED,
  //     WorkStatus.LEAVESECONDHALFCANCELLED,
  //     WorkStatus.ATTENDANCEREGULARIZEREQUESTCANCELLED,
  //     WorkStatus.COMPOFFCANCELLED,
  //     // WorkStatus.ATTENDANCEREGULARIZEREQUEST,
  //     // WorkStatus.COMPOFFAPPLIED,
  //     // WorkStatus.COMPOFFLEAVEAPPLIED,
  //     WorkStatus.COMPOFFLEAVECANCELLED,
  //     WorkStatus.COMPOFFLEAVEREJECTED,
  //     WorkStatus.COMPOFFREJECTED
  //   ]

  //   // 🟡 Group attendances by date
  //   const attendanceMapByDate = new Map<string, ClockInClockOut[]>()

  //   for (const record of attendances) {
  //     // const date = dayjs.utc(record.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD')
  //     const date = dayjs(record.clockIn).format('YYYY-MM-DD')

  //     if (!attendanceMapByDate.has(date)) {
  //       attendanceMapByDate.set(date, [])
  //     }
  //     attendanceMapByDate.get(date)!.push(record)
  //   }

  //   // 🟡 Filter out request events if final status already exists on same day
  //   const filteredAttendances = attendances.filter(record => {
  //     const requestStatus = record.workStatus
  //     // const date = dayjs.utc(record.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD')
  //     const date = dayjs(record.clockIn).format('YYYY-MM-DD')

  //     if (cancelledStatuses.includes(requestStatus as WorkStatus)) return false
  //     // Not a request type
  //     if (!statusOverrideMap[requestStatus]) return true

  //     const sameDayRecords = attendanceMapByDate.get(date) ?? []

  //     return !sameDayRecords.some(r =>
  //       statusOverrideMap[requestStatus].includes(r.workStatus as WorkStatus)
  //     )
  //   })

  //   // ✅ Sort by priority then time
  //   const sortedRecords = [...filteredAttendances].sort((a, b) => {
  //     const aIsHighPriority = highPriorityStatuses.includes(a.workStatus as WorkStatus)
  //     const bIsHighPriority = highPriorityStatuses.includes(b.workStatus as WorkStatus)

  //     if (aIsHighPriority !== bIsHighPriority) {
  //       return aIsHighPriority ? -1 : 1
  //     }

  //     const timeA = new Date(a.clockIn).getTime()
  //     const timeB = new Date(b.clockIn).getTime()
  //     return timeA - timeB
  //   })

  //   // ✅ Map to FullCalendar event format
  //   return sortedRecords
  //     .filter(
  //       record =>
  //         record.workStatus !== WorkStatus.LEAVECANCELLED &&
  //         record.workStatus !== WorkStatus.LEAVEREJECTED
  //     )
  //     .map(record => {
  //       const clockIn = record.clockIn
  //       // ? dayjs
  //       //     .utc(record.clockIn)
  //       //     .tz(this.selectedZoneName)
  //       //     .subtract(5.5, 'hour') // adjust if needed for display
  //       //     .format()
  //       // : undefined

  //       const clockOut = record.clockOut
  //       // ? dayjs.utc(record.clockOut).tz(this.selectedZoneName).subtract(5.5, 'hour').format()
  //       // : undefined

  //       return {
  //         // title: record.workStatus === WorkStatus.CLOCKIN ? 'Clock In/Out' : (record.workStatus === WorkStatus.LEAVE ? record.workStatus (record.leaveType) : record.workStatus) : record.workStatus,
  //         title:
  //           record.workStatus === WorkStatus.CLOCKIN
  //             ? 'Clock In/Out'
  //             : record.workStatus === WorkStatus.LEAVE
  //               ? `${record.workStatus} (${record.leaveType ?? 'N/A'})`
  //               : record.workStatus,

  //         start: clockIn,
  //         end: clockOut,
  //         backgroundColor: statusColorMap[record.workStatus] || '#607D8B',
  //         textColor: 'white',
  //         extendedProps: {
  //           description: record.holiday?.title || '',
  //           duration: record.workHour || '',
  //           title: record.workStatus,
  //           leaveType: record.leaveType,
  //           workStatus: record.workStatus,
  //           lateLogin: record.lateLogin,
  //           earlyLeave: record.earlyLeave,
  //           autoClockOut: record.autoClockOut,
  //           autoClockIn: record.autoClockIn
  //         }
  //       }
  //     })
  // }

  //18 may 2026
  private mapAttendancesToEvents(attendances: ClockInClockOut[]) {
    // if (!attendances) return []
    if (!attendances || attendances.length === 0) return []

    // const joiningRecord = attendances.find(record => record.workStatus === WorkStatus.JOININGDATE)
    // this.employeeJoiningDate = joiningRecord?.clockIn
    //   ? dayjs.utc(joiningRecord.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD')
    //   : ''

    if (this.visibleRangeStart && this.visibleRangeEnd) {
      const start = this.visibleRangeStart.getTime()
      const end = this.visibleRangeEnd.getTime()
      attendances = attendances.filter(r => {
        const t = new Date(r.clockIn).getTime()
        return t >= start && t <= end
      })
    }

    const statusColorMap: Record<string, string> = {
      Present: '#4CAF50',
      Absent: '#F44336',
      Leave: '#FF9800',
      Holiday: '#9C27B0',
      ClockIn: '#03A9F4',
      ClockOut: '#FFC107',
      WeeklyOff: '#795548',
      LeaveFirstHalf: '#FF9800',
      LeaveSecondHalf: '#FF9800',
      DateofJoining: '#1B84FF',
      Break: '#607D8B',
      Compoff: '#8BC34A',
      RegularizePresent: '#2196F3',
      RegularizeRequest: '#03A9F4',
      LeaveRequest: '#FF9800',
      CompoffRequest: '#CDDC39'
    }

    const highPriorityStatuses = [
      WorkStatus.JOININGDATE,
      WorkStatus.JOININGDATETRAINEE,
      WorkStatus.JOININGDATEEMPLOYEE,
      WorkStatus.JOININGDATECONTRACTOR,
      WorkStatus.PRESENT,
      WorkStatus.REGULARIZEPRESENT,
      WorkStatus.REGULARIZEPRESENTHALF,
      WorkStatus.HOLIDAY,
      WorkStatus.LEAVE,
      WorkStatus.ABSENT,
      WorkStatus.WEEKLYOFF,
      WorkStatus.LEAVEFIRSTHALF,
      WorkStatus.LEAVESECONDHALF,
      WorkStatus.LEAVEFIRSTHALFAPPLIED,
      WorkStatus.LEAVEFIRSTHALFREJECTED,
      WorkStatus.LEAVESECONDHALFAPPLIED,
      WorkStatus.LEAVESECONDHALFREJECTED,
      WorkStatus.COMPOFFEARNED,
      WorkStatus.ATTENDANCEREGULARIZEREQUEST,
      WorkStatus.LEAVEREQUEST,
      WorkStatus.COMPOFFAPPLIED,
      WorkStatus.ABSENTFIRSTHALF,
      WorkStatus.ABSENTSECONDHALF,
      WorkStatus.ABSENTHALF,
      WorkStatus.LEAVECANCELREQUESTED,
      WorkStatus.LEAVEFIRSTHALFCANCELREQUESTED,
      WorkStatus.LEAVESECONDHALFCANCELREQUESTED,
      // WorkStatus.LEAVECANCELAPPROVED,
      WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED,
      WorkStatus.LEAVESECONDHALFCANCELAPPROVED,
      WorkStatus.REGULARIZEPRESENTFIRSTHALF,
      WorkStatus.REGULARIZEPRESENTSECONDHALF,
      WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALF,
      WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALF
    ]

    // 🟡 Map of request-type statuses and their override equivalents
    // 🟡 Map of request-type statuses and their override equivalents
    // Updated logic:
    // - Agar leave cancel/reject ho gayi ho to old request hide nahi hogi.
    // - Agar same date par nayi leave request (Sick, Casual, etc.) apply ki gayi ho,
    //   to wahi latest request calendar me show hogi.

    const statusOverrideMap: Record<string, string[]> = {
      [WorkStatus.ATTENDANCEREGULARIZEREQUEST]: [
        WorkStatus.PRESENT,
        WorkStatus.REGULARIZEPRESENT,
        WorkStatus.REGULARIZEPRESENTHALF,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTCANCELLED,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTREJECTED,
        WorkStatus.WEEKLYOFF
      ],

      // Full Day Leave Request
      [WorkStatus.LEAVEREQUEST]: [
        WorkStatus.LEAVE, // approved leave
        WorkStatus.LEAVEFIRSTHALF,
        WorkStatus.LEAVESECONDHALF
        // ❌ LEAVECANCELLED and LEAVEREJECTED intentionally removed
        // so that if previous leave is cancelled/rejected and new leave is applied,
        // the new request will still be visible.
      ],

      // First Half Leave Request
      [WorkStatus.LEAVEFIRSTHALFAPPLIED]: [
        WorkStatus.LEAVEFIRSTHALF
        // ❌ Cancelled/Rejected removed
      ],

      // Second Half Leave Request
      [WorkStatus.LEAVESECONDHALFAPPLIED]: [
        WorkStatus.LEAVESECONDHALF
        // ❌ Cancelled/Rejected removed
      ],

      // Comp Off Request
      [WorkStatus.COMPOFFAPPLIED]: [WorkStatus.COMPOFFEARNED],

      // Comp Off Leave Applied
      [WorkStatus.COMPOFFLEAVEAPPLIED]: [WorkStatus.COMPOFFLEAVEAPPROVE],
      [WorkStatus.LEAVECANCELREQUESTED]: [WorkStatus.LEAVECANCELLED],
      [WorkStatus.LEAVEFIRSTHALFCANCELREQUESTED]: [WorkStatus.LEAVEFIRSTHALFCANCELLED],
      [WorkStatus.LEAVESECONDHALFCANCELREQUESTED]: [WorkStatus.LEAVESECONDHALFCANCELLED],

      // [WorkStatus.LEAVECANCELAPPROVED]: [WorkStatus.LEAVECANCELREQUESTED],
      [WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED]: [WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED],
      [WorkStatus.LEAVESECONDHALFCANCELAPPROVED]: [WorkStatus.LEAVESECONDHALFCANCELAPPROVED]
    }
    // ✅ Leave overrides Absent
    // [WorkStatus.LEAVE]: [WorkStatus.LEAVEREJECTED, WorkStatus.LEAVECANCELLED],
    // [WorkStatus.ABSENT]: [
    //   WorkStatus.PRESENT,
    //   WorkStatus.REGULARIZEPRESENT,
    //   WorkStatus.REGULARIZEPRESENTHALF,
    //   WorkStatus.LEAVE
    // ]

    // [WorkStatus.LEAVEFIRSTHALF]: [WorkStatus.ABSENT],
    // [WorkStatus.LEAVESECONDHALF]: [WorkStatus.ABSENT],
    // }

    const cancelledStatuses: WorkStatus[] = [
      WorkStatus.LEAVECANCELLED,
      WorkStatus.LEAVEFIRSTHALFCANCELLED,
      WorkStatus.LEAVESECONDHALFCANCELLED,
      WorkStatus.ATTENDANCEREGULARIZEREQUESTCANCELLED,
      WorkStatus.COMPOFFCANCELLED,
      // WorkStatus.ATTENDANCEREGULARIZEREQUEST,
      // WorkStatus.COMPOFFAPPLIED,
      // WorkStatus.COMPOFFLEAVEAPPLIED,
      WorkStatus.COMPOFFLEAVECANCELLED,
      WorkStatus.COMPOFFLEAVEREJECTED,
      WorkStatus.COMPOFFREJECTED
    ]

    // 🟡 Group attendances by date
    const attendanceMapByDate = new Map<string, ClockInClockOut[]>()

    for (const record of attendances) {
      // const date = dayjs.utc(record.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD')
      const date = dayjs(record.clockIn).format('YYYY-MM-DD')

      if (!attendanceMapByDate.has(date)) {
        attendanceMapByDate.set(date, [])
      }
      attendanceMapByDate.get(date)!.push(record)
    }

    // 🟡 Filter out request events if final status already exists on same day
    // 🟡 Replace your current filteredAttendances logic with this

    // 🔍 DEBUG VERSION - Is code ko apne filteredAttendances ke place par use karein
    const filteredAttendances = attendances.filter(record => {
      const requestStatus = record.workStatus
      const date = dayjs(record.clockIn).format('YYYY-MM-DD')

      // =========================
      // 1. Hidden statuses
      // =========================
      const hiddenStatuses: WorkStatus[] = [
        WorkStatus.LEAVECANCELLED,
        WorkStatus.LEAVEREJECTED,
        WorkStatus.LEAVEFIRSTHALFCANCELLED,
        WorkStatus.LEAVEFIRSTHALFREJECTED,
        WorkStatus.LEAVESECONDHALFCANCELLED,
        WorkStatus.LEAVESECONDHALFREJECTED,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTCANCELLED,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTREJECTED,
        WorkStatus.COMPOFFCANCELLED,
        WorkStatus.COMPOFFREJECTED,
        WorkStatus.COMPOFFLEAVECANCELLED,
        WorkStatus.COMPOFFLEAVEREJECTED,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALFCANCELLED,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALFREJECTED,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALFCANCELLED,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALFREJECTED,
        WorkStatus.LEAVECANCELAPPROVED,
        WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED,
        WorkStatus.LEAVESECONDHALFCANCELAPPROVED
      ]

      // Cancelled/Rejected kabhi show nahi honge
      if (hiddenStatuses.includes(requestStatus as WorkStatus)) {
        return false
      }

      // Request types only
      const requestTypes: WorkStatus[] = [
        WorkStatus.LEAVEREQUEST,
        WorkStatus.LEAVEFIRSTHALFAPPLIED,
        WorkStatus.LEAVESECONDHALFAPPLIED,
        WorkStatus.ATTENDANCEREGULARIZEREQUEST,
        WorkStatus.COMPOFFAPPLIED,
        WorkStatus.COMPOFFLEAVEAPPLIED,
        WorkStatus.LEAVECANCELREQUESTED,
        WorkStatus.LEAVEFIRSTHALFCANCELREQUESTED,
        WorkStatus.LEAVESECONDHALFCANCELREQUESTED,
        // WorkStatus.LEAVECANCELAPPROVED,
        WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED,
        WorkStatus.LEAVESECONDHALFCANCELAPPROVED,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALF,
        WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALF
      ]

      // Request type nahi hai to direct show karo
      if (!requestTypes.includes(requestStatus as WorkStatus)) {
        return true
      }

      const sameDayRecords = attendanceMapByDate.get(date) ?? []

      // =========================
      // 2. Approved status check
      // =========================
      const approvedStatusMap: Record<string, WorkStatus[]> = {
        [WorkStatus.LEAVEREQUEST]: [
          WorkStatus.LEAVE,
          WorkStatus.LEAVEFIRSTHALF,
          WorkStatus.LEAVESECONDHALF
        ],
        [WorkStatus.LEAVEFIRSTHALFAPPLIED]: [WorkStatus.LEAVEFIRSTHALF],
        [WorkStatus.LEAVESECONDHALFAPPLIED]: [WorkStatus.LEAVESECONDHALF],
        [WorkStatus.ATTENDANCEREGULARIZEREQUEST]: [
          WorkStatus.PRESENT,
          WorkStatus.REGULARIZEPRESENT,
          WorkStatus.REGULARIZEPRESENTHALF
        ],
        [WorkStatus.COMPOFFAPPLIED]: [WorkStatus.COMPOFFEARNED],
        [WorkStatus.COMPOFFLEAVEAPPLIED]: [WorkStatus.COMPOFFLEAVEAPPROVE],
        [WorkStatus.LEAVECANCELREQUESTED]: [],
        [WorkStatus.LEAVEFIRSTHALFCANCELREQUESTED]: [],
        [WorkStatus.LEAVESECONDHALFCANCELREQUESTED]: [],
        // [WorkStatus.LEAVECANCELAPPROVED]: [],
        [WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED]: [],
        [WorkStatus.LEAVESECONDHALFCANCELAPPROVED]: [],
        [WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALF]: [
          WorkStatus.REGULARIZEPRESENTFIRSTHALF,
          WorkStatus.REGULARIZEPRESENT
        ],
        [WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALF]: [
          WorkStatus.REGULARIZEPRESENTSECONDHALF,
          WorkStatus.REGULARIZEPRESENT
        ]
      }

      const approvedStatuses = approvedStatusMap[requestStatus] ?? []

      const hasApproved = sameDayRecords.some(r =>
        approvedStatuses.includes(r.workStatus as WorkStatus)
      )

      if (hasApproved) {
        return false
      }

      // =========================
      // 3. Related cancel/reject statuses
      // =========================
      const cancelStatusMap: Record<string, WorkStatus[]> = {
        [WorkStatus.LEAVEREQUEST]: [WorkStatus.LEAVECANCELLED, WorkStatus.LEAVEREJECTED],
        [WorkStatus.LEAVEFIRSTHALFAPPLIED]: [
          WorkStatus.LEAVEFIRSTHALFCANCELLED,
          // WorkStatus.LEAVEFIRSTHALFREJECTED
          WorkStatus.LEAVECANCELLED,
          WorkStatus.LEAVEREJECTED
        ],
        [WorkStatus.LEAVESECONDHALFAPPLIED]: [
          WorkStatus.LEAVESECONDHALFCANCELLED,
          // WorkStatus.LEAVESECONDHALFREJECTED,
          WorkStatus.LEAVECANCELLED,
          WorkStatus.LEAVEREJECTED
        ],
        [WorkStatus.ATTENDANCEREGULARIZEREQUEST]: [
          WorkStatus.ATTENDANCEREGULARIZEREQUESTCANCELLED,
          WorkStatus.ATTENDANCEREGULARIZEREQUESTREJECTED
        ],
        [WorkStatus.COMPOFFAPPLIED]: [WorkStatus.COMPOFFCANCELLED, WorkStatus.COMPOFFREJECTED],
        [WorkStatus.COMPOFFLEAVEAPPLIED]: [
          WorkStatus.COMPOFFLEAVECANCELLED,
          WorkStatus.COMPOFFLEAVEREJECTED
        ],
        // [WorkStatus.LEAVECANCELREQUESTED]: [WorkStatus.LEAVECANCELLED],
        // [WorkStatus.LEAVEFIRSTHALFCANCELREQUESTED]: [WorkStatus.LEAVEFIRSTHALFCANCELLED],
        // [WorkStatus.LEAVESECONDHALFCANCELREQUESTED]: [WorkStatus.LEAVESECONDHALFCANCELLED],
        // [WorkStatus.LEAVECANCELREQUESTED]: [WorkStatus.LEAVECANCELAPPROVED],
        // [WorkStatus.LEAVEFIRSTHALFCANCELREQUESTED]: [WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED],
        // [WorkStatus.LEAVESECONDHALFCANCELREQUESTED]: [WorkStatus.LEAVESECONDHALFCANCELAPPROVED]

        // [WorkStatus.LEAVECANCELREQUESTED]: [WorkStatus.LEAVECANCELAPPROVED],
        [WorkStatus.LEAVEFIRSTHALFCANCELREQUESTED]: [WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED],
        [WorkStatus.LEAVESECONDHALFCANCELREQUESTED]: [WorkStatus.LEAVESECONDHALFCANCELAPPROVED],
        [WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALF]: [
          WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALFCANCELLED,
          WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALFREJECTED
        ],
        [WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALF]: [
          WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALFCANCELLED,
          WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALFREJECTED
        ]
      }

      const relatedCancelStatuses = cancelStatusMap[requestStatus] ?? []

      // =========================
      // 4. Requests and Cancels
      // =========================
      const sameTypeRequests = sameDayRecords
        .filter(r => r.workStatus === requestStatus)
        .sort((a, b) => new Date(a.clockIn).getTime() - new Date(b.clockIn).getTime())

      const relatedCancels = sameDayRecords
        .filter(r => relatedCancelStatuses.includes(r.workStatus as WorkStatus))
        .sort((a, b) => new Date(a.clockIn).getTime() - new Date(b.clockIn).getTime())

      // // =========================
      // // 5. Active request count
      // // =========================
      // const activeRequestCount = sameTypeRequests.length - relatedCancels.length

      // // Sab cancel ho chuke hain
      // if (activeRequestCount <= 0) {
      //   // console.log('❌ No active request, hiding all')
      //   return false
      // }

      // // Latest request hi show hogi
      // const latestRequest = sameTypeRequests[sameTypeRequests.length - 1]

      // const shouldShow = latestRequest?.id === record.id

      // // console.log(
      // // shouldShow ? '✅ Showing Request ID: ' + record.id : '❌ Hiding Request ID: ' + record.id
      // // )

      // return shouldShow

      // =========================
      // 5. Latest request active or not
      // =========================
      const latestRequest = sameTypeRequests[sameTypeRequests.length - 1]

      // Latest request hide it only if a cancel/reject
      const isLatestCancelled = relatedCancels.some(
        c => new Date(c['createdAt']).getTime() > new Date(latestRequest['createdAt']).getTime()
      )
      if (isLatestCancelled) return false

      return latestRequest?.id === record.id
    })

    // ✅ Sort by priority then time
    const sortedRecords = [...filteredAttendances].sort((a, b) => {
      const aIsHighPriority = highPriorityStatuses.includes(a.workStatus as WorkStatus)
      const bIsHighPriority = highPriorityStatuses.includes(b.workStatus as WorkStatus)

      if (aIsHighPriority !== bIsHighPriority) {
        return aIsHighPriority ? -1 : 1
      }

      const timeA = new Date(a.clockIn).getTime()
      const timeB = new Date(b.clockIn).getTime()
      return timeA - timeB
    })

    // ✅ Map to FullCalendar event format
    return sortedRecords
      .filter(
        record =>
          record.workStatus !== WorkStatus.LEAVECANCELLED &&
          record.workStatus !== WorkStatus.LEAVEREJECTED
        // record.workStatus !== WorkStatus.LEAVECANCELAPPROVED &&
        // record.workStatus !== WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED &&
        // record.workStatus !== WorkStatus.LEAVESECONDHALFCANCELAPPROVED
      )
      .map(record => {
        // const clockIn = record.clockIn
        // const clockIn = record.clockIn
        //   ? dayjs.utc(record.clockIn).tz(this.selectedZoneName).format('YYYY-MM-DD')
        //   : undefined
        const clockIn = record.clockIn
          ? dayjs.utc(record.clockIn).tz(this.selectedZoneName).format()
          : undefined
        // ? dayjs
        //     .utc(record.clockIn)
        //     .tz(this.selectedZoneName)
        //     .subtract(5.5, 'hour') // adjust if needed for display
        //     .format()
        // : undefined

        // const clockOut = record.clockOut
        // const clockOut = record.clockOut
        //   ? dayjs.utc(record.clockOut).tz(this.selectedZoneName).format('YYYY-MM-DD')
        //   : undefined
        const clockOut = record.clockOut
          ? dayjs.utc(record.clockOut).tz(this.selectedZoneName).format()
          : undefined
        const clockInDisplay = record.clockIn
          ? dayjs.utc(record.clockIn).tz(this.selectedZoneName).format('DD/MM/YYYY hh:mm:ss A')
          : 'N/A'

        const clockOutDisplay = record.clockOut
          ? dayjs.utc(record.clockOut).tz(this.selectedZoneName).format('DD/MM/YYYY hh:mm:ss A')
          : 'N/A'
        // ? dayjs.utc(record.clockOut).tz(this.selectedZoneName).subtract(5.5, 'hour').format()
        // : undefined
        // Pehle yeh map banao - kis status ka text white/black hoga
        const textColorMap: Record<string, string> = {
          // WHITE text wale statuses
          [WorkStatus.ABSENT]: 'white',
          [WorkStatus.ABSENTFIRSTHALF]: 'white',
          [WorkStatus.ABSENTSECONDHALF]: 'white',
          [WorkStatus.ABSENTHALF]: 'white',
          [WorkStatus.HOLIDAY]: 'white',
          [WorkStatus.JOININGDATE]: 'white',
          [WorkStatus.JOININGDATEEMPLOYEE]: 'white',
          [WorkStatus.JOININGDATECONTRACTOR]: 'white',
          [WorkStatus.JOININGDATETRAINEE]: 'white',
          [WorkStatus.CLOCKIN]: 'black',
          [WorkStatus.BREAK]: 'black',
          [WorkStatus.WEEKLYOFF]: 'black',

          // BLACK text wale statuses
          [WorkStatus.PRESENT]: 'black',
          [WorkStatus.PRESENTHALF]: 'black',
          [WorkStatus.REGULARIZEPRESENT]: 'black',
          [WorkStatus.REGULARIZEPRESENTHALF]: 'black',
          [WorkStatus.ATTENDANCEREGULARIZEREQUEST]: 'black',
          [WorkStatus.LEAVE]: 'black',
          [WorkStatus.LEAVEFIRSTHALF]: 'black',
          [WorkStatus.LEAVESECONDHALF]: 'black',
          [WorkStatus.LEAVEREQUEST]: 'black',
          [WorkStatus.LEAVEFIRSTHALFAPPLIED]: 'black',
          [WorkStatus.LEAVESECONDHALFAPPLIED]: 'black',
          [WorkStatus.COMPOFFAPPLIED]: 'black',
          [WorkStatus.COMPOFFEARNED]: 'black',
          [WorkStatus.COMPOFFLEAVEAPPLIED]: 'black',
          [WorkStatus.COMPOFFLEAVEAPPROVE]: 'black',
          [WorkStatus.LATE]: 'white',
          [WorkStatus.EARLY]: 'white',
          [WorkStatus.ABSENTHALFDUETOLATE]: 'white',
          [WorkStatus.LEAVECANCELREQUESTED]: 'black',
          [WorkStatus.LEAVEFIRSTHALFCANCELREQUESTED]: 'black',
          [WorkStatus.LEAVESECONDHALFCANCELREQUESTED]: 'black',
          // [WorkStatus.LEAVECANCELAPPROVED]: 'black',
          [WorkStatus.LEAVEFIRSTHALFCANCELAPPROVED]: 'black',
          [WorkStatus.LEAVESECONDHALFCANCELAPPROVED]: 'black',
          [WorkStatus.REGULARIZEPRESENTFIRSTHALF]: 'black',
          [WorkStatus.REGULARIZEPRESENTSECONDHALF]: 'black',
          [WorkStatus.ATTENDANCEREGULARIZEREQUESTFIRSTHALF]: 'black',
          [WorkStatus.ATTENDANCEREGULARIZEREQUESTSECONDHALF]: 'black'
        }
        return {
          id: record.id,
          // title: record.workStatus === WorkStatus.CLOCKIN ? 'Clock In/Out' : (record.workStatus === WorkStatus.LEAVE ? record.workStatus (record.leaveType) : record.workStatus) : record.workStatus,
          title:
            record.workStatus === WorkStatus.CLOCKIN
              ? 'Clock In/Out'
              : record.workStatus === WorkStatus.LEAVE
                ? `${record.workStatus} (${record.leaveType ?? 'N/A'})`
                : record.workStatus,

          start: clockIn,
          end: clockOut,
          // backgroundColor: statusColorMap[record.workStatus] || '#607D8B',
          // textColor: 'white',
          // ✅ Har status ka apna text color
          textColor: textColorMap[record.workStatus as WorkStatus] ?? 'black',
          extendedProps: {
            description: record.holiday?.title || '',
            duration: record.workHour || '',
            title: record.workStatus,
            leaveType: record.leaveType,
            workStatus: record.workStatus,
            lateLogin: record.lateLogin,
            earlyLeave: record.earlyLeave,
            autoClockOut: record.autoClockOut,
            autoClockIn: record.autoClockIn,
            clockInDisplay,
            clockOutDisplay
          }
        }
      })
  }

  onBusinessChange(businessId: string) {
    // this.dates.clear()
    if (businessId) {
      this.selectedEmployeeId = ''
      this.store.dispatch(resetEmployeeState())
      // Call a method to fetch data based on the selected owner
      const params = `businessId=${encodeURIComponent(businessId)}&disabled=false`

      // this.store.dispatch(loadEmployees({ params })) // Dispatch action with filter

      setTimeout(() => {
        this.store.dispatch(loadEmployees({ params }))
      }, 0)
    } else {
      this.selectedEmployeeId = ''
      this.store.dispatch(resetEmployeeState())
      this.onEmployeeChange('')
    }
  }

  onEmployeeChange(employeeId: string) {
    // this.dates.clear()
    const dates = this.form?.get('dates') as FormArray

    if (dates) {
      dates.clear()
    }
    // Reset calendar/form control
    if (this.form && this.form.get('daterange')) {
      this.form.get('daterange')?.reset() // Clear selection
      this.form.get('daterange')?.disable() // Optionally disable until employee selected
    }

    if (!employeeId) {
      // No employee selected, just exit
      this.employeeJoiningDate = ''
      this.minDateTime = ''
      this.attendanceOffCanvas = true
      this.store.dispatch(clearAttendanceState())
      const calendarApi = this.calendarComponent?.getApi?.()
      if (calendarApi) {
        calendarApi.removeAllEvents()
      }
      return
    }
    this.store.dispatch(clearAttendanceState())
    if (employeeId) {
      this.employees$
        .subscribe(employees => {
          const selectedEmployee = employees.find(emp => emp.id === employeeId)
          if (selectedEmployee && selectedEmployee.joiningDate) {
            // If joiningDate can be Date or string, coerce to string:
            this.employeeJoiningDate =
              typeof selectedEmployee.joiningDate === 'string'
                ? selectedEmployee.joiningDate
                : selectedEmployee.joiningDate.toISOString() // Or .toLocaleDateString(), etc.
            this.minDateTime = this.formatForDateTimeLocal(new Date(this.employeeJoiningDate))
          }
          // this.form.get('daterange')?.enable()
        })
        .unsubscribe() // Unsubscribe immediately to avoid memory leak

      // minDateTime = dayjs(this.employeeJoiningDate).toDate()

      this.validRangeSet = false
      // First clear the state
      this.store.dispatch(clearAttendanceState())
      // Call a method to fetch data based on the selected owner
      const params: any = {}
      params.userId = employeeId
      this.employeeId = employeeId
      this.selectedEmployeeId = employeeId
      // this.store.dispatch(
      //   loadEmployeeAttendance({
      //     id: employeeId,
      //     // params: null
      //     params: new URLSearchParams(params).toString()
      //   })
      // )

      // const calendarApi = this.calendarComponent?.getApi()
      // if (!calendarApi) return
      // // ✅ Move calendar to current month
      // calendarApi.today()
      // // ✅ Get current visible range from calendar
      // const view = calendarApi.view

      // const start = view.currentStart
      // const end = view.currentEnd

      // // Adjust end (FullCalendar currentEnd is exclusive)
      // const adjustedEnd = new Date(end.getTime() - 1)
      // // 🚀 Tell datesSet to ignore the next trigger
      // this.skipNextDatesSet = true
      // this.loadAttendances(start, adjustedEnd)
      this.calanderRerender()

      // const calendarApi = this.calendarComponent?.getApi()
      // if (calendarApi) {
      //   calendarApi.refetchEvents() // ✅ triggers datesSet
      // }
    }
    // else {
    //   const now = new Date()
    //   const start = new Date(now.getFullYear(), now.getMonth(), 1)
    //   const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    //   this.loadAttendances(start, end)
    // }
  }

  loadAllBusinesses() {
    const params: any = {}
    if (this.businessIdss?.length) {
      params.businessIds = this.businessIdss.join(',')
    }

    params.disabled = false
    this.store.dispatch(
      loadBusinesses({
        params: new URLSearchParams(params).toString()
      })
    )
  }

  customSearchFn = (term: string, item: any) => {
    const fullName = item && item.businessName ? item.businessName.toLowerCase() : ''
    return fullName.includes(term ? term.toLowerCase() : '')
  }

  // customSearchFnEmployee = (term: string, item: any) => {
  //   const fullName = `${item.firstName} ${item.lastName}`.toLowerCase()
  //   return fullName.includes(term ? term.toLowerCase() : '')
  // }

  customSearchFnEmployee = (term: string, item: any) => {
    // Handle undefined item and fields gracefully
    const first = item?.firstName || ''
    const last = item?.lastName || ''
    const fullName = `${first} ${last}`.toLowerCase()

    return fullName.includes(term?.toLowerCase() || '')
  }

  //gps
  // regularizeAttendanceDrawer(type: string) {
  //   this.offcanvasTitle = type
  //   if (this.selectedEmployeeId) {
  //     this.attendancePolicyService
  //       .assignedPolicyByUserId(this.selectedEmployeeId)
  //       .subscribe(policy => {
  //         const startTime = policy?.shiftStartTime ?? '09:00'
  //         const endTime = policy?.shiftEndTime ?? '18:00'

  //         $('#dateRangeInputModal')
  //           .daterangepicker({
  //             autoUpdateInput: true,
  //             minDate: dayjs(this.employeeJoiningDate).toDate(), // ✅ Disables dates before joining

  //             maxDate: dayjs().toDate(), // ⛔ Disables future dates

  //             locale: {
  //               format: 'DD/MM/YYYY',
  //               cancelLabel: 'Clear'
  //             }
  //           })
  //           // const config = this.getDateRangePickerConfig('', '')
  //           // $('#dateRangeInputCompoffModal').daterangepicker(config)
  //           // $('#dateRangeInputCompoffModal')
  //           //   .daterangepicker(config)
  //           .on('apply.daterangepicker', (ev: any, picker: any) => {
  //             const formattedStart = picker.startDate.format('DD/MM/YYYY')
  //             const formattedEnd = picker.endDate.format('DD/MM/YYYY')

  //             $('#dateRangeInputModal').val(`${formattedStart} - ${formattedEnd}`)

  //             let start = picker.startDate.clone()
  //             const end = picker.endDate.clone()
  //             $('#dateAccordion').html('')
  //             // this.dates.clear()

  //             if (this.form && this.form.get('dates') instanceof FormArray) {
  //               this.dates.clear()
  //             }
  //             // while (start.unix() <= end.unix()) {
  //             //   const currentDateStr = start.format('DD/MM/YYYY')
  //             //   const dateInputFormat = start.format('YYYY-MM-DD') // for datetime-local
  //             //   this.addAccordionItem(
  //             //     currentDateStr,
  //             //     dateInputFormat,
  //             //     startTime,
  //             //     endTime,
  //             //     'dateAccordion'
  //             //   )
  //             //   start = start.add(1, 'day')
  //             // }
  //             //check for duplicate date
  //             while (start.unix() <= end.unix()) {
  //               const currentDateStr = start.format('DD/MM/YYYY')
  //               const dateInputFormat = start.format('YYYY-MM-DD') // for datetime-local

  //               // ✅ Check if date already exists in FormArray
  //               const dateAlreadyExists = this.dates.controls.some(ctrl => {
  //                 const existingDate = ctrl.get('date')?.value
  //                 return existingDate === dateInputFormat
  //               })

  //               if (!dateAlreadyExists) {
  //                 this.addAccordionItem(
  //                   currentDateStr,
  //                   dateInputFormat,
  //                   startTime,
  //                   endTime,
  //                   'dateAccordion'
  //                 )
  //               }

  //               start = start.add(1, 'day')
  //             }
  //           })
  //       })
  //   }
  // }

  // checkRegularizeEligibility(data: ClockInClockOutResponse[]) {
  //   const allDayRecords = data.flatMap(item => item.dayRecords || [])
  //   const hasAbsent = allDayRecords.some(
  //     r =>
  //       r.workStatus === this.workStatusEnum.ABSENT ||
  //       r.workStatus === this.workStatusEnum.ABSENTFIRSTHALF ||
  //       r.workStatus === this.workStatusEnum.ABSENTSECONDHALF ||
  //       r.workStatus === this.workStatusEnum.ABSENTHALF
  //   )

  //   this.attendanceOffCanvas = !hasAbsent
  // }

  checkRegularizeEligibility(data: ClockInClockOutResponse[]) {
    const allDayRecords = data.flatMap(item => item.dayRecords || [])

    //  Group records by date to detect fully-covered half-day regularizations
    const recordsByDate = new Map<string, ClockInClockOut[]>()

    allDayRecords.forEach(record => {
      const dateKey = dayjs(record.clockIn).format('YYYY-MM-DD')
      if (!recordsByDate.has(dateKey)) recordsByDate.set(dateKey, [])
      recordsByDate.get(dateKey)!.push(record)
    })

    //  A date is eligible if:
    //   1. It has a plain Absent / Absent-Half record, OR
    //   2. It has a Half-Day regularization request that is NOT yet fully covered
    //      (i.e. only First Half OR only Second Half requested, not both)
    const hasEligibleDate = Array.from(recordsByDate.values()).some(records => {
      const hasPlainAbsent = records.some(
        r =>
          r.workStatus === this.workStatusEnum.ABSENT ||
          r.workStatus === this.workStatusEnum.ABSENTFIRSTHALF ||
          r.workStatus === this.workStatusEnum.ABSENTSECONDHALF ||
          r.workStatus === this.workStatusEnum.ABSENTHALF
      )
      if (hasPlainAbsent) return true

      const hasFirstHalfReq = records.some(
        r => r.workStatus === this.workStatusEnum.ATTENDANCEREGULARIZEREQUESTFIRSTHALF
      )
      const hasSecondHalfReq = records.some(
        r => r.workStatus === this.workStatusEnum.ATTENDANCEREGULARIZEREQUESTSECONDHALF
      )

      //  Enable only when exactly one half is requested (other half still pending)
      if (hasFirstHalfReq && !hasSecondHalfReq) return true
      if (hasSecondHalfReq && !hasFirstHalfReq) return true

      return false
    })

    this.attendanceOffCanvas = !hasEligibleDate
  }

  //12 january 2026
  regularizeAttendanceDrawer(type: string) {
    this.offcanvasTitle = type

    const userId = this.selectedEmployeeId || this.user?.id

    // if (!this.selectedEmployeeId) return
    if (!userId) {
      return
    }

    this.attendancePolicyService
      // .assignedPolicyByUserId(this.selectedEmployeeId)
      .assignedPolicyByUserId(userId)
      .subscribe(policy => {
        const startTime = policy?.shiftStartTime ?? '09:00'
        const endTime = policy?.shiftEndTime ?? '18:00'

        $('#dateRangeInputModal')
          .daterangepicker({
            autoUpdateInput: true,
            minDate: dayjs(this.employeeJoiningDate).toDate(), // disable dates before joining
            maxDate: dayjs().toDate(), // disable future dates
            locale: {
              format: 'DD/MM/YYYY',
              cancelLabel: 'Clear'
            }
          })
          .on('apply.daterangepicker', (ev: any, picker: any) => {
            const formattedStart = picker.startDate.format('DD/MM/YYYY')
            const formattedEnd = picker.endDate.format('DD/MM/YYYY')

            $('#dateRangeInputModal').val(`${formattedStart} - ${formattedEnd}`)

            // Clear previous accordion items
            $('#dateAccordion').html('')
            if (this.form && this.form.get('dates') instanceof FormArray) {
              this.dates.clear()
            }

            // Get all dates in the selected range
            let start = picker.startDate.clone()
            const end = picker.endDate.clone()
            const selectedDates: string[] = []
            while (start.unix() <= end.unix()) {
              selectedDates.push(start.format('YYYY-MM-DD'))
              start = start.add(1, 'day')
            }

            // Filter attendance data for Absent or AbsentForHalf
            this.attendances$?.pipe(take(1)).subscribe(data => {
              const allDayRecords = data.flatMap(item => item.dayRecords || [])

              const absentRecords = allDayRecords
                .filter(
                  r =>
                    r.workStatus === this.workStatusEnum.ABSENT ||
                    r.workStatus === this.workStatusEnum.ABSENTFIRSTHALF ||
                    r.workStatus === this.workStatusEnum.ABSENTSECONDHALF ||
                    r.workStatus === this.workStatusEnum.ABSENTHALF
                )
                .filter(r => this.isOnOrAfterJoiningDate(r.clockIn))

              const seenDates = new Set<string>()

              absentRecords.forEach(record => {
                const recordDate = dayjs(record.clockIn).format('YYYY-MM-DD')

                // Only include if within selected range
                if (!selectedDates.includes(recordDate)) return

                // Skip duplicates
                if (seenDates.has(recordDate)) return
                seenDates.add(recordDate)

                const displayDate = dayjs(record.clockIn).format('DD/MM/YYYY')
                this.addAccordionItem(
                  displayDate,
                  recordDate,
                  startTime,
                  endTime,
                  'dateAccordion',
                  record.workStatus
                )
              })
            })
          })
        this.loadDefaultAttendanceRegularization(startTime, endTime)
      })
  }

  private loadDefaultAttendanceRegularization(startTime: string, endTime: string) {
    $('#dateAccordion').html('')

    if (this.form && this.form.get('dates') instanceof FormArray) {
      this.dates.clear()
    }

    this.attendances$?.pipe(take(1)).subscribe(data => {
      const allDayRecords = data.flatMap(item => item.dayRecords || [])

      // const absentRecords = allDayRecords
      //   .filter(
      //     r =>
      //       r.workStatus === this.workStatusEnum.ABSENT ||
      //       r.workStatus === this.workStatusEnum.ABSENTFIRSTHALF ||
      //       r.workStatus === this.workStatusEnum.ABSENTSECONDHALF ||
      //       r.workStatus === this.workStatusEnum.ABSENTHALF
      //   )
      //   .filter(r => this.isOnOrAfterJoiningDate(r.clockIn))

      //  Include half-day requests where the OTHER half is still pending
      const absentRecords = allDayRecords
        .filter(
          r =>
            r.workStatus === this.workStatusEnum.ABSENT ||
            r.workStatus === this.workStatusEnum.ABSENTFIRSTHALF ||
            r.workStatus === this.workStatusEnum.ABSENTSECONDHALF ||
            r.workStatus === this.workStatusEnum.ABSENTHALF ||
            //  Half-day regularization requests (only when other half pending)
            (r.workStatus === this.workStatusEnum.ATTENDANCEREGULARIZEREQUESTFIRSTHALF &&
              !allDayRecords.some(
                other =>
                  other.id !== r.id &&
                  dayjs(other.clockIn).isSame(dayjs(r.clockIn), 'day') &&
                  other.workStatus === this.workStatusEnum.ATTENDANCEREGULARIZEREQUESTSECONDHALF
              )) ||
            (r.workStatus === this.workStatusEnum.ATTENDANCEREGULARIZEREQUESTSECONDHALF &&
              !allDayRecords.some(
                other =>
                  other.id !== r.id &&
                  dayjs(other.clockIn).isSame(dayjs(r.clockIn), 'day') &&
                  other.workStatus === this.workStatusEnum.ATTENDANCEREGULARIZEREQUESTFIRSTHALF
              ))
        )
        .filter(r => this.isOnOrAfterJoiningDate(r.clockIn))

      const clockInDates = absentRecords
        .map(r => dayjs(r.clockIn))
        .sort((a, b) => a.unix() - b.unix())

      const defaultStartDate = clockInDates[0]?.toDate()
      const defaultEndDate = clockInDates[clockInDates.length - 1]?.toDate()

      const seenDates = new Set<string>()

      absentRecords.forEach(record => {
        const recordDate = dayjs(record.clockIn).format('YYYY-MM-DD')

        if (seenDates.has(recordDate)) {
          return
        }

        seenDates.add(recordDate)

        this.addAccordionItem(
          dayjs(record.clockIn).format('DD/MM/YYYY'),
          recordDate,
          startTime,
          endTime,
          'dateAccordion',
          record.workStatus
        )
      })

      const picker = $('#dateRangeInputModal').data('daterangepicker')

      if (picker && defaultStartDate && defaultEndDate) {
        picker.setStartDate(defaultStartDate)
        picker.setEndDate(defaultEndDate)

        $('#dateRangeInputModal').val(
          `${dayjs(defaultStartDate).format('DD/MM/YYYY')} - ${dayjs(defaultEndDate).format('DD/MM/YYYY')}`
        )
      }
    })
  }

  // addAccordionItem(
  //   dateStr: string,
  //   dateInputFormat: string,
  //   startTime: string,
  //   endTime: string,
  //   id: string
  // ) {
  //   const itemId = `accordionItem${Date.now()}${Math.random().toString(36).substr(2, 5)}`
  //   const collapseId = `collapse${itemId}`

  //   const startDateTime = `${dateInputFormat}T${startTime}`
  //   const endDateTime = `${dateInputFormat}T${endTime}`

  //   const html = `
  //     <div class="accordion-item mb-2" id="${itemId}">
  //       <h2 class="accordion-header d-flex justify-content-between align-items-center px-3">
  //         <button class="accordion-button collapsed flex-grow-1" type="button" data-bs-toggle="collapse" data-bs-target="#${collapseId}">
  //           ${dateStr}
  //         </button>
  //         <button class="btn btn-sm btn-outline-danger ms-2 delete-btn" data-id="${itemId}" title="Delete">
  //           <i class="ti ti-trash"></i>
  //         </button>
  //       </h2>
  //       <div id="${collapseId}" class="accordion-collapse collapse">
  //         <div class="accordion-body d-flex gap-2 align-items-center">
  //           <input type="datetime-local" class="form-control date-start" value="${startDateTime}" />
  //           <input type="datetime-local" class="form-control date-end" value="${endDateTime}" />
  //         </div>
  //       </div>
  //     </div>`

  //   $(`#${id}`).append(html)

  //   // Push to FormArray
  //   this.dates.push(
  //     this.fb.group({
  //       itemId,
  //       date: [dateStr],
  //       startDateTime: [startDateTime],
  //       endDateTime: [endDateTime]
  //     })
  //   )
  // }

  //11 sep 2025
  //   addAccordionItem(
  //     dateStr: string,
  //     dateInputFormat: string,
  //     startTime: string,
  //     endTime: string,
  //     containerId: string
  //   ) {
  //     const itemId = `accordionItem${Date.now()}${Math.random().toString(36).substr(2, 5)}`
  //     const row: DateRowPayload = {
  //       itemId,
  //       date: dateStr,
  //       startTime,
  //       endTime,
  //       selectionType: 'HalfDay', // Default: HalfDay
  //       halfDayOption: 'FirstHalf' // Default: FirstHalf
  //     }
  //     dateRows.push(row)

  //     const wrapper = document.createElement('div')
  //     wrapper.className = 'accordion-item mb-2'
  //     wrapper.id = itemId

  //     wrapper.innerHTML = `
  //   <h2 class="accordion-header d-flex justify-content-between align-items-center px-3">
  //     <button class="accordion-button collapsed flex-grow-1" type="button" data-bs-toggle="collapse" data-bs-target="#collapse${itemId}">
  //       ${dateStr}
  //     </button>
  //     <button class="btn btn-sm btn-outline-danger ms-2 delete-btn" data-id="${itemId}" title="Delete">
  //       <i class="ti ti-trash"></i>
  //     </button>
  //   </h2>
  //   <div id="collapse${itemId}" class="accordion-collapse collapse">
  //     <div class="accordion-body d-flex gap-2 align-items-center">
  //       <div class="ms-3">
  //         <div class="form-check form-check-inline">
  //           <input type="radio" name="dayOption${itemId}" class="form-check-input" value="FullDay" checked>
  //           <label class="form-check-label">Full Day</label>
  //         </div>
  //         <div class="form-check form-check-inline">
  //           <input type="radio" name="dayOption${itemId}" class="form-check-input" value="HalfDay" >
  //           <label class="form-check-label">Half Day</label>
  //         </div>
  //         <div class="ms-5 mb-2 half-day-options" style="display:none">
  //           <div class="form-check form-check-inline">
  //             <input type="radio" name="halfDayOption${itemId}" class="form-check-input" value="FirstHalf">
  //             <label class="form-check-label">First Half</label>
  //           </div>
  //           <div class="form-check form-check-inline">
  //             <input type="radio" name="halfDayOption${itemId}" class="form-check-input" value="SecondHalf">
  //             <label class="form-check-label">Second Half</label>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   </div>
  // `

  //     const fullRadio = wrapper.querySelector(
  //       `input[type=radio][value=FullDay]`
  //     ) as HTMLInputElement | null
  //     const halfRadio = wrapper.querySelector(
  //       `input[type=radio][value=HalfDay]`
  //     ) as HTMLInputElement | null
  //     const fhRadio = wrapper.querySelector(
  //       `input[type=radio][value=FirstHalf]`
  //     ) as HTMLInputElement | null
  //     const shRadio = wrapper.querySelector(
  //       `input[type=radio][value=SecondHalf]`
  //     ) as HTMLInputElement | null
  //     const halfOptions = wrapper.querySelector('.half-day-options') as HTMLDivElement | null

  //     // Listeners for toggling and syncing values
  //     if (fullRadio && halfOptions) {
  //       fullRadio.addEventListener('change', () => {
  //         if (fullRadio.checked) {
  //           halfOptions.style.display = 'none'
  //           row.selectionType = 'FullDay'
  //           row.halfDayOption = null
  //         }
  //       })
  //     }
  //     if (halfRadio && halfOptions) {
  //       halfRadio.addEventListener('change', () => {
  //         if (halfRadio.checked) {
  //           halfOptions.style.display = 'block'
  //           row.selectionType = 'HalfDay'
  //           // Keep last selected half, or default to FirstHalf if none
  //           if (!row.halfDayOption) {
  //             row.halfDayOption = 'FirstHalf'
  //             if (fhRadio) fhRadio.checked = true
  //           }
  //         }
  //       })
  //     }
  //     if (fhRadio) {
  //       fhRadio.addEventListener('change', () => {
  //         if (fhRadio.checked) row.halfDayOption = 'FirstHalf'
  //       })
  //     }
  //     if (shRadio) {
  //       shRadio.addEventListener('change', () => {
  //         if (shRadio.checked) row.halfDayOption = 'SecondHalf'
  //       })
  //     }

  //     document.getElementById(containerId)?.appendChild(wrapper)

  //     // If you also want to push to Reactive FormArray...
  //     const startDateTime = `${dateInputFormat}T${startTime}`
  //     const endDateTime = `${dateInputFormat}T${endTime}`
  //     this.dates.push(
  //       this.fb.group({
  //         itemId,
  //         date: [dateStr],
  //         startDateTime: [startDateTime],
  //         endDateTime: [endDateTime],
  //         selectionType: ['HalfDay'], // default to HalfDay
  //         halfDayOption: ['FirstHalf'] // default to FirstHalf
  //       })
  //     )
  //   }

  //26 sep 2025
  /**
   * Add accordion dynamically
   */
  addAccordionItem(
    dateStr: string,
    dateInputFormat: string,
    startTime: string,
    endTime: string,
    containerId: string,
    workStatus = 'Absent'
  ) {
    const itemId = `accordionItem${Date.now()}${Math.random().toString(36).substr(2, 5)}`

    const startDateTime = `${dateInputFormat}T${startTime}`
    const endDateTime = `${dateInputFormat}T${endTime}`

    // Get Day Name
    const dayName = this.getDayName(dateStr)

    const selectionType: 'FullDay' | 'HalfDay' =
      workStatus === WorkStatus.ABSENTFIRSTHALF ||
      workStatus === WorkStatus.ABSENTSECONDHALF ||
      workStatus === WorkStatus.ABSENTHALF
        ? 'HalfDay'
        : 'FullDay'

    const halfDayOption: 'FirstHalf' | 'SecondHalf' | null =
      workStatus === WorkStatus.ABSENTSECONDHALF
        ? 'SecondHalf'
        : selectionType === 'HalfDay'
          ? 'FirstHalf'
          : null

    // Create form group for this accordion
    const formGroup = this.fb.group({
      itemId: [itemId],
      date: [dateStr],
      day: [dayName],
      startDateTime: [startDateTime],
      endDateTime: [endDateTime],
      // selectionType: ['FullDay'], // default
      // halfDayOption: this.fb.control<string | null>(null)
      selectionType: [selectionType],
      halfDayOption: this.fb.control<string | null>(halfDayOption)
    })
    this.dates.push(formGroup)

    // Wrapper container
    const wrapper = document.createElement('div')
    wrapper.className = 'accordion-item mb-2'
    wrapper.id = itemId

    wrapper.innerHTML = `
      <h2 class="accordion-header d-flex justify-content-between align-items-center px-3">
        <button class="accordion-button collapsed flex-grow-1" type="button" data-bs-toggle="collapse" data-bs-target="#collapse${itemId}">
          ${dateStr} <span class="badge bg-light text-dark ms-2">${dayName}</span>
        </button>
        <button class="btn btn-sm btn-outline-danger ms-2 delete-btn" data-id="${itemId}" title="Delete">
          <i class="ti ti-trash"></i>
        </button>
      </h2>
      <div id="collapse${itemId}" class="accordion-collapse collapse">
        <div class="accordion-body d-flex gap-2 align-items-center">
          <div class="ms-3">
            <!-- Full / Half Day -->
            <div class="form-check form-check-inline">
              <input type="radio" name="dayOption-${itemId}" class="form-check-input" value="FullDay" ${selectionType === 'FullDay' ? 'checked' : ''}>
              <label class="form-check-label">Full Day</label>
            </div>
            <div class="form-check form-check-inline">
              <input type="radio" name="dayOption-${itemId}" class="form-check-input" value="HalfDay" ${selectionType === 'HalfDay' ? 'checked' : ''}>
              <label class="form-check-label">Half Day</label>
            </div>

            <!-- Half Day Options -->
            <div class="ms-5 mb-2 half-day-options" style="display:${selectionType === 'HalfDay' ? 'block' : 'none'}">
              <div class="form-check form-check-inline">
                <input type="radio" name="halfDayOption-${itemId}" class="form-check-input" value="FirstHalf" ${halfDayOption === 'FirstHalf' ? 'checked' : ''}>
                <label class="form-check-label">First Half</label>
              </div>
              <div class="form-check form-check-inline">
                <input type="radio" name="halfDayOption-${itemId}" class="form-check-input" value="SecondHalf" ${halfDayOption === 'SecondHalf' ? 'checked' : ''}>
                <label class="form-check-label">Second Half</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    // Handle radio logic
    const fullRadio = wrapper.querySelector(`input[type=radio][value=FullDay]`) as HTMLInputElement
    const halfRadio = wrapper.querySelector(`input[type=radio][value=HalfDay]`) as HTMLInputElement
    const fhRadio = wrapper.querySelector(`input[type=radio][value=FirstHalf]`) as HTMLInputElement
    const shRadio = wrapper.querySelector(`input[type=radio][value=SecondHalf]`) as HTMLInputElement
    const halfOptions = wrapper.querySelector('.half-day-options') as HTMLDivElement

    // Full Day selection
    if (fullRadio) {
      fullRadio.addEventListener('change', () => {
        if (fullRadio.checked) {
          halfOptions.style.display = 'none'
          formGroup.patchValue({
            selectionType: 'FullDay',
            halfDayOption: null
          })
        }
      })
    }

    // Half Day selection
    if (halfRadio) {
      halfRadio.addEventListener('change', () => {
        if (halfRadio.checked) {
          halfOptions.style.display = 'block'
          formGroup.patchValue({
            selectionType: 'HalfDay',
            halfDayOption: formGroup.value.halfDayOption || 'FirstHalf'
          })
          if (!formGroup.value.halfDayOption && fhRadio) {
            fhRadio.checked = true
          }
        }
      })
    }

    if (fhRadio) {
      fhRadio.addEventListener('change', () => {
        if (fhRadio.checked) {
          formGroup.patchValue({ halfDayOption: 'FirstHalf' })
        }
      })
    }

    if (shRadio) {
      shRadio.addEventListener('change', () => {
        if (shRadio.checked) {
          formGroup.patchValue({ halfDayOption: 'SecondHalf' })
        }
      })
    }

    // Delete button
    const deleteBtn = wrapper.querySelector('.delete-btn') as HTMLButtonElement
    deleteBtn.addEventListener('click', () => {
      this.deleteAccordionItem(itemId)
    })

    // Append wrapper to DOM
    // this.renderer.appendChild(this.accordionContainer.nativeElement, wrapper)
    document.getElementById(containerId)?.appendChild(wrapper)
  }

  /**
   * Delete Accordion Item
   */
  deleteAccordionItem(itemId: string) {
    const index = this.dates.value.findIndex((item: any) => item.itemId === itemId)
    if (index !== -1) {
      this.dates.removeAt(index)
    }

    const itemEl = document.getElementById(itemId)
    if (itemEl) {
      itemEl.remove()
    }
  }

  /**
   * Helper: Get Day Name
   */
  private getDayName(dateStr: string): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const [day, month, year] = dateStr.split('/') // expected format DD/MM/YYYY
    const date = new Date(+year, +month - 1, +day)
    return days[date.getDay()]
  }

  /**
   * Submit Form
   */
  submitForm(type: string) {
    const formValues = this.form.value.dates

    const request = formValues.map((item: any) => {
      // const [day, month, year] = item.date.split('/') // date is DD/MM/YYYY
      // const jsDate = new Date(+year, +month - 1, +day)

      return {
        date: item.date,
        // day: jsDate.toLocaleDateString('en-US', { weekday: 'long' }),
        startTime: item.startDateTime,
        endTime: item.endDateTime,
        selectionType: item.selectionType,
        halfDayOption: item.halfDayOption
      }
    })
    if (this.user) {
      const payload = {
        request,
        userId: this.employeeId,
        requestBy: this.user.id,
        requestType: type
      }
      // this.store.dispatch(createEmployeeRequest({ payload }))
      // console.log('Final Payload:', payload)
      this.store.dispatch(
        createEmployeeRequest({
          userId: this.employeeId || this.user.id,
          requestBy: this.user.id, // or some other user if needed
          // approverId: this.approverId,
          requestType: type,
          request
        })
      )

      setTimeout(() => {
        $('#dateAccordion').html('')
        $('#dateCompoffAccordion').html('')
        if (this.form?.get('dates') instanceof FormArray) {
          this.dates.clear()
        }
        this.closeAllOffcanvas()
        this.calanderRerender()
      }, 1500)
    }
  }

  // submitForm(type: string) {
  //   const datesArray = this.form.get('dates') as FormArray

  //   if (datesArray.length === 0) {
  //     alert('Please add at least one date before submitting.')
  //     return
  //   }

  //   if (this.form.invalid || this.dates.length === 0) {
  //     // console.warn('Form is invalid or has no entries')
  //     return
  //   }

  //   if (this.user) {
  //     const payload = {
  //       userId: this.employeeId || this.user.id,
  //       requestBy: this.user.id, // or some other user if needed
  //       // approverId: this.approverId,
  //       requestType: type,
  //       // request: this.dates.controls.map(control => ({
  //       //   date: control.get('date')?.value,
  //       //   startTime: control.get('startDateTime')?.value,
  //       //   endTime: control.get('endDateTime')?.value,
  //       //   selectionType: control.get('selectionType')?.value, // 'FullDay' | 'HalfDay'
  //       //   halfDayOption: control.get('halfDayOption')?.value ?? null // 'FirstHalf' | 'SecondHalf' | null
  //       // }))
  //       request: dateRows
  //     }
  //     // console.log('payload', payload)
  //     // this.employeeRequestService.submitRegularizedAttendance(payload).subscribe({
  //     //   next: response => {
  //     //     console.log('Success:', response)
  //     //     // Optionally reset form or close drawer
  //     //     this.form.reset()
  //     //     this.dates.clear() // Clear FormArray entries
  //     //   },
  //     //   error: err => {
  //     //     console.error('Failed to submit:', err)
  //     //   }
  //     // })
  //     this.store.dispatch(
  //       createEmployeeRequest({
  //         userId: this.employeeId || this.user.id,
  //         requestBy: this.user.id, // or some other user if needed
  //         // approverId: this.approverId,
  //         requestType: type,
  //         request: this.dates.controls.map(control => ({
  //           date: control.get('date')?.value,
  //           startTime: control.get('startDateTime')?.value,
  //           endTime: control.get('endDateTime')?.value,
  //           selectionType: control.get('selectionType')?.value, // 'FullDay' | 'HalfDay'
  //           halfDayOption: control.get('halfDayOption')?.value ?? null // 'FirstHalf' | 'SecondHalf' | null
  //         }))
  //       })
  //     )
  //   }
  // }

  ngAfterViewInit(): void {
    this.deleteHandler = (e: JQuery.ClickEvent) => {
      e.stopPropagation()
      const itemId = $(e.currentTarget).data('id')
      $('#' + itemId).remove()

      // remove from FormArray
      const index = this.dates.controls.findIndex(ctrl => ctrl.get('itemId')?.value === itemId)
      if (index > -1) this.dates.removeAt(index)
    }

    $(document).on('click', '.delete-btn', this.deleteHandler)
  }

  ngOnDestroy(): void {
    $(document).off('click', '.delete-btn', this.deleteHandler)
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  openOffcanvas(content: TemplateRef<any>) {
    this.offcanvasRef = this.offcanvasService.open(content, { position: 'end' }) // or 'start'
  }

  closeOffcanvas() {
    if (this.offcanvasRef) {
      this.offcanvasRef.close()
      this.offcanvasService.dismiss()
    }
  }

  closeAllOffcanvas() {
    const openedOffcanvasElements = document.querySelectorAll('.offcanvas.show')
    openedOffcanvasElements.forEach((element: Element) => {
      const offcanvasInstance = bootstrap.Offcanvas.getInstance(element)
      if (offcanvasInstance) {
        offcanvasInstance.hide()
        // offcanvasInstance.close()
      }
    })
  }

  // dayCellDidMount(arg: any) {
  //   const today = new Date().toISOString().split('T')[0] // 'YYYY-MM-DD'
  //   const cellDate = arg.date.toISOString().split('T')[0] // Convert arg.date

  //   if (cellDate === today) {
  //     arg.el.classList.add('circle-highlight')
  //   }
  // }

  // dayCellDidMount(arg: any) {
  //   const today = new Date()
  //   const istToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  //     .toISOString()
  //     .split('T')[0] // 'YYYY-MM-DD' in IST (local midnight)

  //   const cellDate = new Date(arg.date.getFullYear(), arg.date.getMonth(), arg.date.getDate())
  //     .toISOString()
  //     .split('T')[0] // 'YYYY-MM-DD'

  //   if (cellDate === istToday) {
  //     arg.el.classList.add('circle-highlight')
  //   }
  // }

  requestCompoffDrawer(type: string) {
    this.offcanvasTitle = type
    if (this.user) {
      this.attendancePolicyService
        .assignedPolicyByUserId(this.selectedEmployeeId)
        .subscribe(policy => {
          const startTime = policy?.shiftStartTime ?? '09:00'
          const endTime = policy?.shiftEndTime ?? '18:00'
          // console.log('min date 2', this.employeeJoiningDate)
          $('#dateRangeInputCompoffModal')
            .daterangepicker({
              autoUpdateInput: true,
              minDate: dayjs(this.employeeJoiningDate).toDate(), // ✅ Disables dates before joining

              maxDate: dayjs().toDate(), // ⛔ Disables future dates

              locale: {
                format: 'DD/MM/YYYY',
                cancelLabel: 'Clear'
              }
            })
            // const config = this.getDateRangePickerConfig('', '')
            // $('#dateRangeInputCompoffModal').daterangepicker(config)
            // $('#dateRangeInputCompoffModal')
            //   .daterangepicker(config)
            .on('apply.daterangepicker', (ev: any, picker: any) => {
              const formattedStart = picker.startDate.format('DD/MM/YYYY')
              const formattedEnd = picker.endDate.format('DD/MM/YYYY')

              $('#dateRangeInputCompoffModal').val(`${formattedStart} - ${formattedEnd}`)

              let start = picker.startDate.clone()
              const end = picker.endDate.clone()
              $('#dateCompoffAccordion').html('')
              // this.dates.clear()
              if (this.form && this.form.get('dates') instanceof FormArray) {
                this.dates.clear()
              }

              // while (start.unix() <= end.unix()) {
              //   const currentDateStr = start.format('DD/MM/YYYY')
              //   const dateInputFormat = start.format('YYYY-MM-DD') // for datetime-local
              //   this.addAccordionItem(
              //     currentDateStr,
              //     dateInputFormat,
              //     startTime,
              //     endTime,
              //     'dateCompoffAccordion'
              //   )
              //   start = start.add(1, 'day')
              // }
              //check for duplicate dates
              while (start.unix() <= end.unix()) {
                const currentDateStr = start.format('DD/MM/YYYY')
                const dateInputFormat = start.format('YYYY-MM-DD') // for datetime-local

                // ✅ Check if date already exists in FormArray
                const dateAlreadyExists = this.dates.controls.some(ctrl => {
                  const existingDate = ctrl.get('date')?.value
                  return existingDate === dateInputFormat
                })

                if (!dateAlreadyExists) {
                  this.addAccordionItem(
                    currentDateStr,
                    dateInputFormat,
                    startTime,
                    endTime,
                    'dateCompoffAccordion'
                  )
                }

                start = start.add(1, 'day')
              }
            })
        })
    }
  }

  onCountryChange(selectedCountryId: number) {
    const selectedCountry = this.countries.find(c => c.id === selectedCountryId)
    this.timezones = selectedCountry?.timezones || []
    this.cdr.detectChanges() // force update if needed
  }

  // customSearchFnCountry = (term: string, item: { name: string }) => {
  //   term = term.toLowerCase()
  //   return item.name.toLowerCase().includes(term)
  // }
  // If you want to also allow searching by timezone:
  customSearchFnCountry = (term: string, item: Country) => {
    const search = term.toLowerCase()
    const inName = item.name.toLowerCase().includes(search)
    const inTimezone = item.timezones?.some(
      tz => tz.zoneName.toLowerCase().includes(search) || tz.tzName.toLowerCase().includes(search)
    )
    return inName || inTimezone
  }

  // Optional: Search by country or zone
  customSearchFnTimezone = (term: string, item: TimezoneOption) => {
    const search = term.toLowerCase()
    return (
      item.countryName.toLowerCase().includes(search) ||
      item.zoneName.toLowerCase().includes(search)
    )
  }

  onTimezoneChange(zone: any) {
    this.selectedZoneName = zone.zoneName
    // console.log('this.selectedZonename', this.selectedZoneName)
    const calendarApi = this.calendarComponent?.getApi?.()
    if (calendarApi) {
      // console.log('zone', this.selectedZoneName)

      calendarApi.setOption('timeZone', this.selectedZoneName)
      calendarApi.refetchEvents?.()
      calendarApi.removeAllEvents()

      // ✅ Safe check for attendances$
      if (!this.attendances$) {
        // console.warn('Attendance data not available.')
        return
      }

      this.attendances$.subscribe(data => {
        $('#dateAccordion').html('')
        $('#dateCompoffAccordion').html('')

        if (this.form?.get('dates') instanceof FormArray) {
          this.dates.clear()
        }

        const allDayRecords = data.flatMap(item => item.dayRecords || [])
        const absentRecords = allDayRecords
          .filter(
            r =>
              r.workStatus === 'Absent' ||
              r.workStatus === this.workStatusEnum.ABSENTFIRSTHALF ||
              r.workStatus === this.workStatusEnum.ABSENTSECONDHALF ||
              r.workStatus === this.workStatusEnum.ABSENTHALF
          )
          .filter(r => this.isOnOrAfterJoiningDate(r.clockIn))

        // Group records by date string (you can also include userId if needed)
        // const recordsByDate = allDayRecords.reduce(
        //   (acc, record) => {
        //     const dateKey = new Date(record.clockIn).toISOString().slice(0, 10) // 'YYYY-MM-DD'

        //     if (!acc[dateKey]) {
        //       acc[dateKey] = []
        //     }

        //     acc[dateKey].push(record)
        //     return acc
        //   },
        //   {} as Record<string, any[]>
        // )

        // const absentRecords = Object.values(recordsByDate).flatMap(recordsForDay => {
        //   const hasOtherEvents = recordsForDay.some(r =>
        //     [
        //       WorkStatus.PRESENT,
        //       WorkStatus.HOLIDAY,
        //       WorkStatus.WEEKLYOFF,
        //       WorkStatus.REGULARIZEPRESENT,
        //       WorkStatus.LEAVE
        //     ].includes(r.workStatus)
        //   )

        //   // If no other events exist, include Absent(s)
        //   if (!hasOtherEvents) {
        //     return recordsForDay.filter(r => r.workStatus === WorkStatus.ABSENT)
        //   }

        //   // Otherwise, exclude Absent(s), return the other events
        //   return recordsForDay.filter(r =>
        //     [
        //       WorkStatus.PRESENT,
        //       WorkStatus.HOLIDAY,
        //       WorkStatus.WEEKLYOFF,
        //       WorkStatus.REGULARIZEPRESENT,
        //       WorkStatus.LEAVE
        //     ].includes(r.workStatus)
        //   )
        // })
        // const clockInDates = absentRecords
        //   .map(record => dayjs(record.clockIn))
        //   .sort((a, b) => a.unix() - b.unix())
        const clockInDates = absentRecords
          .map(record => dayjs.utc(record.clockIn).tz(this.selectedZoneName)) // ⏰ Convert from UTC to selected timezone
          .sort((a, b) => a.unix() - b.unix())

        const defaultStartDate = clockInDates[0]?.toDate()
        const defaultEndDate = clockInDates[clockInDates.length - 1]?.toDate()

        const events = this.mapAttendancesToEvents(allDayRecords)

        calendarApi.removeAllEvents()
        calendarApi.addEventSource(events)

        // if (this.employeeJoiningDate) {
        //   calendarApi.setOption('validRange', {
        //     start: this.employeeJoiningDate
        //   })
        // }

        if (!this.user || absentRecords.length === 0) return
        this.attendanceOffCanvas = true
        this.attendanceOffCanvas = !this.user || absentRecords.length === 0
        this.attendancePolicyService
          .assignedPolicyByUserId(this.selectedEmployeeId ?? this.user.id)
          .subscribe(policy => {
            const startTime = policy?.shiftStartTime ?? '09:00'
            const endTime = policy?.shiftEndTime ?? '18:00'
            const seenDates = new Set<string>()
            absentRecords.forEach(record => {
              const currentDateStr = dayjs(record.clockIn).format('DD/MM/YYYY')
              if (seenDates.has(currentDateStr)) return // Skip duplicates
              const dateInputFormat = dayjs(record.clockIn).format('YYYY-MM-DD')
              this.addAccordionItem(
                currentDateStr,
                dateInputFormat,
                startTime,
                endTime,
                'dateAccordion',
                record.workStatus
              )
            })

            setTimeout(() => {
              $('#dateRangeInputModal').daterangepicker({
                autoUpdateInput: true,
                startDate: defaultStartDate,
                endDate: defaultEndDate,
                minDate: dayjs(this.employeeJoiningDate).toDate(),
                maxDate: dayjs().toDate(),
                locale: {
                  format: 'DD/MM/YYYY',
                  cancelLabel: 'Clear'
                }
              })
              // const config = this.getDateRangePickerConfig(defaultStartDate, defaultEndDate)
              // $('#dateRangeInputCompoffModal').daterangepicker(config)
              // $('#dateRangeInputCompoffModal').daterangepicker(config)
            }, 0)
          })
      })

      // console.log('Current timeZone:', calendarApi?.view?.calendar?.getOption('timeZone'))
    }
  }

  loadAssignedLeavePolicy(userId: any) {
    this.leavePolicyService.assignedPolicyByUserId(userId).subscribe({
      next: (assignedPolicies: any[]) => {
        // Save or use the assigned policies if needed
        this.assignedLeavePolicies = assignedPolicies

        // Check if any policy has leaveType 'Comp Off'
        this.compoffVisible = assignedPolicies.some(
          policy => policy.leavePolicy?.leaveType === LeaveType.COMPOFF
        )
      },
      error: err => {
        // console.error('Failed to load assigned leave policies:', err)
      }
    })
  }

  // onClose() {
  //   this.dates.clear()
  //   // $('#dateAccordion').html('')
  //   // $('#dateCompoffAccordion').html('')
  // }

  openBusinessModal(content: TemplateRef<any>, options: any = {}) {
    this.modalRef = this.modalService.open(content, {
      scrollable: false,
      centered: true,
      size: 'md',
      ...options
    })
    this.title = 'Add'
  }

  onClose() {
    // this.dates.clear()
    // if (this.form) {
    //   this.form.reset(); // Reset the form if it is defined
    // }
    // this.modalService.dismissAll();
    // this.currentStep = 1;

    try {
      if (this.form || this.eventForm) {
        this.form.reset()
        this.eventForm.reset()
      }
      this.rangeForm?.reset()
      this.dates.clear()

      $('#dateRangeInputModal').val('').data('daterangepicker')?.setStartDate(dayjs().toDate())
      $('#dateRangeInputCompoffModal')
        .val('')
        .data('daterangepicker')
        ?.setStartDate(dayjs().toDate())

      $('#dateAccordion').html('')
      $('#dateCompoffAccordion').html('')
      if (this.modalRef) {
        this.modalRef.close()
      }
      this.modalService.dismissAll()
    } catch (error) {
      // console.error('Error closing modal:', error)
    }
  }

  onSubmit() {
    if (this.eventForm.valid && !this.eventId) {
      // handle aliases if needed
      // const timezoneAliases: Record<string, string> = {
      //   'Asia/Calcutta': 'Asia/Kolkata',
      //   'Etc/UTC': 'UTC'
      // }

      // let tz = this.selectedZoneName
      // if (timezoneAliases[tz]) tz = timezoneAliases[tz]

      const tz = TIMEZONE_ALIASES[this.selectedZoneName] ?? this.selectedZoneName

      // current time in selected timezone
      const clockInInSelectedTZ = dayjs().tz(tz)

      // convert to UTC as JS Date
      const clockInUTC: Date = clockInInSelectedTZ.utc().toDate()

      // console.log('ClockIn UTC:', clockInUTC.toISOString())
      // Example output: 2025-12-16T04:16:14.000Z
      this.store.dispatch(
        createEvent({
          workStatus: this.eventForm.get('workStatus')?.value,
          userId: this.employeeId,
          lateLogin: this.eventForm.get('lateLogin')?.value,
          clockIn: this.eventForm.get('clockIn')?.value,
          clockOut: this.eventForm.get('clockOut')?.value,
          timeZone: this.selectedZoneName
          // clockIn: clockInUTC
        })
      )
    }
    // else if (this.eventForm.valid && this.eventId) {
    //   this.store.dispatch(
    //     updateEvent({
    //       id: this.eventId,
    //       departmentName: this.eventForm.get('departmentName')?.value,
    //       businessId: this.eventForm.get('businessId')?.value
    //     })
    //   )
    // }
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.eventForm.get(controlName)
    if (control && control.touched && control.errors) {
      for (const errorName in control.errors) {
        if (this.errorMessages[errorName]) {
          return this.errorMessages[errorName](control.errors[errorName])
        }
      }
    }
    return null
  }

  errorMessages: Record<string, (error?: any) => string> = {
    required: () => 'This field is required',
    minlength: (e: any) => `Minimum length is ${e.requiredLength}`,
    durationFormat: () => 'Format must be like 1h 30m' // 👈 add this
    // ...other errors
  }

  private durationHmValidator(): ValidatorFn {
    const regex = /^(\d+\s*h\s*)?(\d+\s*m\s*)?$/i
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value || '').toString().trim()
      if (!value) return null
      return regex.test(value) ? null : { durationFormat: true }
    }
  }

  get isPermission(): boolean {
    const allowedRoles = ['Admin', 'Business Owner', 'HR'] // adjust names to match DB

    return (
      this.user?.userRoles?.some((ur: any) => ur.role && allowedRoles.includes(ur.role?.name)) ??
      false
    )
  }

  private formatForDateTimeLocal(date: Date): string {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    const hh = String(date.getHours()).padStart(2, '0')
    const mm = String(date.getMinutes()).padStart(2, '0')
    return `${y}-${m}-${d}T${hh}:${mm}`
  }

  // setSystemTimezone() {
  //   const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  //   console.log('System timezone:', systemTimezone)

  //   const matchedTimezone = this.timezoneOptions.find(tz => tz.zoneName === systemTimezone)

  //   if (matchedTimezone) {
  //     this.selectedZoneName = matchedTimezone // for dropdown binding
  //   } else {
  //     console.warn('System timezone not found in list')
  //   }
  // }

  setSystemTimezone() {
    const exists = this.timezoneOptions.some(tz => tz.zoneName === this.selectedZoneName)

    if (!exists) {
      this.selectedZoneName = 'UTC' // fallback
    }
  }

  // private getDateRangePickerConfig(startDate: any, endDate: any) {
  //   return {
  //     autoUpdateInput: true,
  //     startDate,
  //     endDate,
  //     minDate: dayjs(this.employeeJoiningDate).toDate(),
  //     maxDate: dayjs().toDate(),
  //     locale: {
  //       format: 'DD/MM/YYYY',
  //       cancelLabel: 'Clear'
  //     }
  //   }
  // }

  private calanderRerender() {
    const calendarApi = this.calendarComponent?.getApi()
    if (!calendarApi) return
    // ✅ Move calendar to current month
    // calendarApi.today()
    // ✅ Get current visible range from calendar
    const view = calendarApi.view

    const start = view.currentStart
    const end = view.currentEnd

    // Adjust end (FullCalendar currentEnd is exclusive)
    const adjustedEnd = new Date(end.getTime() - 1)
    // 🚀 Tell datesSet to ignore the next trigger
    this.skipNextDatesSet = true
    this.loadAttendances(start, adjustedEnd)
  }

  private calanderRerenderToDate(dateStr?: string) {
    setTimeout(() => {
      const calendarApi = this.calendarComponent?.getApi()
      if (!calendarApi) return

      if (dateStr) {
        calendarApi.gotoDate(dateStr)
      } else {
        calendarApi.today()
      }

      const view = calendarApi.view
      const start = view.currentStart
      const end = new Date(view.currentEnd.getTime() - 1)

      this.skipNextDatesSet = true
      this.loadAttendances(start, end)
    }, 300)
  }

  eventTypes = [
    {
      label: 'Late',
      value: this.workStatusEnum.LATE
    },
    {
      label: 'Early',
      value: this.workStatusEnum.EARLY
    },
    {
      label: 'Absent for Late',
      value: this.workStatusEnum.ABSENTHALFDUETOLATE
    },
    {
      label: 'Present',
      value: this.workStatusEnum.PRESENT
    },
    {
      label: 'Absent',
      value: this.workStatusEnum.ABSENT
    },
    {
      label: 'Break',
      value: this.workStatusEnum.BREAK
    },
    {
      label: 'Clock In/Out',
      value: this.workStatusEnum.CLOCKIN
    },
    {
      label: 'Weekly Off',
      value: this.workStatusEnum.WEEKLYOFF
    },
    {
      label: 'Holiday',
      value: this.workStatusEnum.HOLIDAY
    },
    {
      label: 'Present for Half Day',
      value: this.workStatusEnum.PRESENTHALF
    },
    {
      label: 'Absent for Half Day',
      value: this.workStatusEnum.ABSENTHALF
    }
  ]

  private hasPermission(
    requiredPermissions: Record<string, string[]> | Record<string, string[]>[]
  ): boolean {
    const required = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions]

    return required.some(set =>
      Object.entries(set).every(([module, actions]) =>
        actions.every(action => this.userPermissions[module]?.includes(action))
      )
    )
  }

  // get hasOnlyAttendanceViewPermission(): boolean {
  //   const attendancePerms: string[] = this.userPermissions['attendance'] || []
  //   console.log('All userPermissions:', this.userPermissions)
  //   console.log('attendance perms:', attendancePerms)
  //   const hasView = attendancePerms.includes('view')
  //   const hasOtherPermission = attendancePerms.some(p => p !== 'view')
  //   return hasView && !hasOtherPermission
  // }

  get hasOnlyAttendanceViewPermission(): boolean {
    const attendancePerms: string[] = this.userPermissions['attendance'] || []
    const hasView = attendancePerms.includes('view')
    const hasActionPermission = attendancePerms.some(
      p => p === 'create' || p === 'edit' || p === 'add'
    )
    return hasView && !hasActionPermission
  }

  private isOnOrAfterJoiningDate(dateValue: string | Date): boolean {
    if (!this.employeeJoiningDate) return true
    const recordDate = dayjs(dateValue).format('YYYY-MM-DD')
    const joiningDate = dayjs(this.employeeJoiningDate).format('YYYY-MM-DD')
    return recordDate >= joiningDate
  }

  get effectiveMaxDateTime(): string | null {
    const status = this.eventForm?.get('workStatus')?.value
    if (status === this.workStatusEnum.HOLIDAY || status === this.workStatusEnum.WEEKLYOFF) {
      return null
    }
    return this.maxDateTime
  }
}
